import { Injectable, OnDestroy } from "@angular/core";
import { MonitoringProductImageService } from "@domain/product/monitoring/monitoring-product-image.service";
import { MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { UtilityProductService } from "@domain/product/monitoring/utility-product.service";
import { ProductService } from "@domain/product/product.service";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { AirPath } from "@domain/project/floorplan/air-path";
import { Floorplan, ProductTypesInGroup } from "@domain/project/floorplan/floorplan";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanFreeText } from "@domain/project/floorplan/floorplan-free-text";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { FloorplanImage } from "@domain/project/floorplan/floorplan-image";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanNode } from "@domain/project/floorplan/floorplan-node";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { FloorplanRemoteSensor } from "@domain/project/floorplan/floorplan-remote-sensor";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanPlaceholderType, FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanDisplayMode, FloorplanSelectionMode, FloorplanState } from "@domain/project/floorplan/floorplan-state";
import { FloorplanText } from "@domain/project/floorplan/floorplan-text";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { MeasurementLine } from "@domain/project/floorplan/measurement-line";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanKvmReceiver } from "@domain/project/floorplan/monitoring/floorplan-kvm-receiver";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import { ReferenceMeasure } from "@domain/project/floorplan/reference-measure";
import { CableRoute } from "@domain/project/floorplan/wiring/cable-route";
import { Pipeline } from "@domain/project/floorplan/wiring/pipeline";
import { ProtectiveTube } from "@domain/project/floorplan/wiring/protective-tube";
import { Wire } from "@domain/project/floorplan/wiring/wire";
import { DangerArea } from "@domain/project/floorplan/zones/danger-area";
import { DustExZone } from "@domain/project/floorplan/zones/dust-ex-zone";
import { ExZone } from "@domain/project/floorplan/zones/ex-zone";
import { IsolationRoom } from "@domain/project/floorplan/zones/isolation-room";
import { ItRoom } from "@domain/project/floorplan/zones/it-room";
import { OperatingTheatre } from "@domain/project/floorplan/zones/operating-theatre";
import { ProcedureRoom } from "@domain/project/floorplan/zones/procedure-room";
import { StandardRoom } from "@domain/project/floorplan/zones/standard-room";
import { WildcardZone } from "@domain/project/floorplan/zones/wildcard-zone";
import { ProjectImageService } from "@domain/project/project-image.service";
import { FloorplanWorkspace } from "@project/floorplanner/floorplan-workspace";
import { AirPathKonva } from "@project/floorplanner/konva/air-path-konva";
import { CableRouteKonva } from "@project/floorplanner/konva/cable-route-konva";
import { CoverageAreaConeKonva } from "@project/floorplanner/konva/coverage-area-cone-konva";
import { FlameDetectorKonva } from "@project/floorplanner/konva/flame-detector-konva";
import { FloorplanKonva } from "@project/floorplanner/konva/floorplan-konva";
import { FreeTextKonva } from "@project/floorplanner/konva/free-text-konva";
import { GroupKonva } from "@project/floorplanner/konva/group-konva";
import { IconKonva } from "@project/floorplanner/konva/icon-konva";
import {
  IconColor,
  ImageFactoryKonva,
  ImageType,
  ProductImageTypes,
  recolorSvgDataUrl,
} from "@project/floorplanner/konva/image-factory-konva";
import { ImageKonva } from "@project/floorplanner/konva/image-konva";
import { KvmKonva } from "@project/floorplanner/konva/kvm-konva";
import { MeasurementLineKonva } from "@project/floorplanner/konva/measurement-line-konva";
import { PipelineKonva } from "@project/floorplanner/konva/pipeline-konva";
import { ProtectiveTubeKonva } from "@project/floorplanner/konva/protective-tube-konva";
import { ReferenceMeasureData, ReferenceMeasureKonva } from "@project/floorplanner/konva/reference-measure-konva";
import { TransformerKonva } from "@project/floorplanner/konva/transformer-konva";
import { TransmitterKonva } from "@project/floorplanner/konva/transmitter-konva";
import { WireKonva } from "@project/floorplanner/konva/wire-konva";
import { DangerAreaKonva } from "@project/floorplanner/konva/zones/danger-area-konva";
import { DustExZoneKonva } from "@project/floorplanner/konva/zones/dust-ex-zone-konva";
import { ExZoneKonva } from "@project/floorplanner/konva/zones/ex-zone-konva";
import { IsolationRoomKonva } from "@project/floorplanner/konva/zones/isolation-room-konva";
import { ItRoomKonva } from "@project/floorplanner/konva/zones/it-room-konva";
import { OperatingTheatreKonva } from "@project/floorplanner/konva/zones/operating-theatre-konva";
import { ProcedureRoomKonva } from "@project/floorplanner/konva/zones/procedure-room-konva";
import { StandardRoomKonva } from "@project/floorplanner/konva/zones/standard-room-konva";
import { WildcardZoneKonva } from "@project/floorplanner/konva/zones/wildcard-zone-konva";
import { ZoneKonva } from "@project/floorplanner/konva/zones/zone-konva";
import { FloorplanDragDropService } from "@utils/floorplan-drag-drop.service";
import { Point } from "@utils/point";
import { ClassConstructor } from "class-transformer";
import Konva from "konva";
import { Group } from "konva/lib/Group";
import { catchError, forkJoin, map, Observable, of, shareReplay, Subject, switchMap } from "rxjs";
import { FloorplanItemTextKonva } from "./floorplan-item-text-konva";

export enum ItemSelectIntention {
  REGULAR,
  EXTENDED_VIEW,
}

export interface FloorplanExportLayers {
  backgroundDataUrl: string;
  overlayDataUrl: string;
}

@Injectable()
export class FloorplanWorkspaceKonvaAdapter implements FloorplanWorkspace, OnDestroy {
  static readonly OBJECT_KEY = "floorplanObject";
  private static readonly EXPORT_PIXEL_RATIOS = [3, 2, 1] as const;

  private temporaryDisabledNodes: Konva.Node[] = [];

  private floorplanLayer = new Konva.Layer();
  private itemLayer = new Konva.Layer();

  private transformer = new TransformerKonva();

  private stage!: Konva.Stage;
  private floorplanShape!: Konva.Shape;
  private floorplanBorderShape!: Konva.Rect;

  private imageGroup = new Konva.Group();
  private floorplanTextGroup = new Konva.Group();
  private freeTextGroup = new Konva.Group();
  private exZoneGroup = new Konva.Group();
  private dustExZoneGroup = new Konva.Group();
  private wildcardZoneGroup = new Konva.Group();
  private dangerAreaGroup = new Konva.Group();
  private measurementLineGroup = new Konva.Group();
  private airPathGroup = new Konva.Group();
  private transmitterGroup = new Konva.Group();
  private alarmDeviceGroup = new Konva.Group();
  private gasWarningCenterGroup = new Konva.Group();
  private signalElementGroup = new Konva.Group();
  private plasticSignGroup = new Konva.Group();
  private pipelineGroup = new Konva.Group();
  private cableRouteGroup = new Konva.Group();
  private protectiveTubeGroup = new Konva.Group();
  private wireGroup = new Konva.Group();
  private productGroups = new Konva.Group();
  private referenceMeasure = new Konva.Group();

  private patientMonitorGroup = new Konva.Group();
  private centralStationGroup = new Konva.Group();
  private telemetryGroup = new Konva.Group();
  private kvmGroup = new Konva.Group();
  private displayGroup = new Konva.Group();
  private utilityGroup = new Konva.Group();
  private flameDetectorGroup = new Konva.Group();
  private flameDetectorConeGroup = new Konva.Group();
  private isolationRoomGroup = new Konva.Group();
  private itRoomGroup = new Konva.Group();
  private operatingTheatreGroup = new Konva.Group();
  private procedureRoomGroup = new Konva.Group();
  private standardRoomGroup = new Konva.Group();

  private monitoringAreaVisible = false;
  private coverageAreaConeVisible = false;

  private selectedNode?: Konva.Node;
  private selectedNodes = new Set<Konva.Node>();
  private _selectedItemChanged$ = new Subject<{ item: FloorplanItem | null; selectIntention: ItemSelectIntention | null }>();
  private _selectedItemsChanged$ = new Subject<FloorplanItem[]>();

  private multiSelectBorders = new Map<Konva.Node, Konva.Rect>();

  private selectionRect: Konva.Rect | null = null;
  private selectionRectStart: { x: number; y: number } | null = null;
  private isDragSelecting = false;
  private suppressNextClick = false;
  private selectionRectMouseMoveHandler: ((e: MouseEvent) => void) | null = null;
  private selectionRectMouseUpHandler: ((e: MouseEvent) => void) | null = null;

  private lastDist: any = 0;
  private lastCenter: any = null;

  private nodesByFloorplanItem = new Map<FloorplanItem, Konva.Node>();
  private nodesByKvmReceiver = new Map<FloorplanKvmReceiver, Konva.Node>();
  private nodesByRemoteSensor = new Map<FloorplanRemoteSensor, Konva.Node>();
  private pendingSelection?: { item: FloorplanItem; selectIntention: ItemSelectIntention };
  private imageFactory!: ImageFactoryKonva;
  private pendingProductImageLoads: Observable<unknown>[] = [];
  private pendingFloorplanImageLoads: Observable<unknown>[] = [];

  private floorplan!: Floorplan;

  /* eslint-disable @angular-eslint/prefer-inject*/
  constructor(
    private dragDropService: FloorplanDragDropService,
    private productService: ProductService,
    private monitoringProductService: MonitoringProductService,
    private monitoringProductImageService: MonitoringProductImageService,
    private projectImageService: ProjectImageService,
    private utilityProductService: UtilityProductService,
  ) {}
  /* eslint-enable @angular-eslint/prefer-inject */

  private groupHandlers = [
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanAlarmDevice,
      addFn: (p: FloorplanAlarmDevice, group?: Konva.Group) => this.addAlarmDevice(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanAlarmDevice, floorplanState.alarmDevicesVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.alarmDevicesItemTextVisible),
      ungroupedProducts: this.alarmDeviceGroup,
      imageType: ImageType.ALARM_DEVICE_ICON,
    },
    {
      match: (p: ProductTypesInGroup) =>
        p instanceof FloorplanGasWarningCenter || p instanceof FloorplanGasWarningCenterPlaceholder,
      addFn: (p: FloorplanGasWarningCenter | FloorplanGasWarningCenterPlaceholder, group?: Konva.Group) =>
        this.addGasWarningCenter(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanGasWarningCenter, floorplanState.gasWarningCentersVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.gasWarningCentersItemTextVisible),
      ungroupedProducts: this.gasWarningCenterGroup,
      imageType: ImageType.GAS_WARNING_CENTER_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanPlasticSign,
      addFn: (p: FloorplanPlasticSign, group?: Konva.Group) => this.addPlasticSign(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanPlasticSign, floorplanState.plasticSignsVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.plasticSignsItemTextVisible),
      ungroupedProducts: this.plasticSignGroup,
      imageType: ImageType.PLASTIC_SIGN_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanSignalElement,
      addFn: (p: FloorplanSignalElement, group?: Konva.Group) => this.addSignalElement(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanSignalElement, floorplanState.signalElementsVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.signalElementsItemTextVisible),
      ungroupedProducts: this.signalElementGroup,
      imageType: ImageType.SIGNAL_ELEMENT_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanTransmitter || p instanceof FloorplanTransmitterPlaceholder,
      addFn: (p: FloorplanTransmitter | FloorplanTransmitterPlaceholder, group?: Konva.Group) =>
        this.addTransmitter(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanTransmitter, floorplanState.transmittersVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.transmittersItemTextVisible),
      ungroupedProducts: this.transmitterGroup,
      imageType: ImageType.TRANSMITTER_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanPatientMonitor,
      addFn: (p: FloorplanPatientMonitor, group?: Konva.Group) => this.addPatientMonitor(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanPatientMonitor, floorplanState.patientMonitorsVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.patientMonitorsItemTextVisible),
      ungroupedProducts: this.patientMonitorGroup,
      imageType: ImageType.PATIENT_MONITOR_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanCentralStation,
      addFn: (p: FloorplanCentralStation, group?: Konva.Group) => this.addCentralStation(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanCentralStation, floorplanState.centralStationsVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.centralStationsItemTextVisible),
      ungroupedProducts: this.centralStationGroup,
      imageType: ImageType.CENTRAL_STATION_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanTelemetry,
      addFn: (p: FloorplanTelemetry, group?: Konva.Group) => this.addTelemetry(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanTelemetry, floorplanState.telemetriesVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.telemetriesItemTextVisible),
      ungroupedProducts: this.telemetryGroup,
      imageType: ImageType.TELEMETRY_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanUtility,
      addFn: (p: FloorplanUtility, group?: Konva.Group) => this.addUtility(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanUtility, floorplanState.utilitiesVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.utilitiesItemTextVisible),
      ungroupedProducts: this.utilityGroup,
      imageType: ImageType.UTILITY_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanKvm,
      addFn: (p: FloorplanKvm, group?: Konva.Group) => this.addKvm(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanKvm, floorplanState.kvmsVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.kvmsItemTextVisible),
      ungroupedProducts: this.kvmGroup,
      imageType: ImageType.KVM_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanDisplay,
      addFn: (p: FloorplanDisplay, group?: Konva.Group) => this.addDisplay(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanDisplay, floorplanState.displaysVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.displaysItemTextVisible),
      ungroupedProducts: this.displayGroup,
      imageType: ImageType.DISPLAY_ICON,
    },
    {
      match: (p: ProductTypesInGroup) =>
        p instanceof FloorplanSimplePlaceholder && p.type === FloorplanPlaceholderType.ALARM_DEVICE,
      addFn: (p: FloorplanSimplePlaceholder, group?: Konva.Group) => this.addAlarmDevice(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanAlarmDevice, floorplanState.alarmDevicesVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.alarmDevicesItemTextVisible),
      ungroupedProducts: this.alarmDeviceGroup,
      imageType: ImageType.ALARM_DEVICE_PLACEHOLDER_ICON,
    },
    {
      match: (p: ProductTypesInGroup) =>
        p instanceof FloorplanSimplePlaceholder && p.type === FloorplanPlaceholderType.PLASTIC_SIGN,
      addFn: (p: FloorplanSimplePlaceholder, group?: Konva.Group) => this.addPlasticSign(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanPlasticSign, floorplanState.plasticSignsVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.plasticSignsItemTextVisible),
      ungroupedProducts: this.plasticSignGroup,
      imageType: ImageType.PLASTIC_SIGN_PLACEHOLDER_ICON,
    },
    {
      match: (p: ProductTypesInGroup) =>
        p instanceof FloorplanSimplePlaceholder && p.type === FloorplanPlaceholderType.SIGNAL_ELEMENT,
      addFn: (p: FloorplanSimplePlaceholder, group?: Konva.Group) => this.addSignalElement(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanSignalElement, floorplanState.signalElementsVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.signalElementsItemTextVisible),
      ungroupedProducts: this.signalElementGroup,
      imageType: ImageType.SIGNAL_ELEMENT_PLACEHOLDER_ICON,
    },
    {
      match: (p: ProductTypesInGroup) =>
        p instanceof FloorplanSimplePlaceholder && p.type === FloorplanPlaceholderType.PATIENT_MONITOR,
      addFn: (p: FloorplanSimplePlaceholder, group?: Konva.Group) => this.addPatientMonitor(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanPatientMonitor, floorplanState.patientMonitorsVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.patientMonitorsItemTextVisible),
      ungroupedProducts: this.patientMonitorGroup,
      imageType: ImageType.PATIENT_MONITOR_PLACEHOLDER_ICON,
    },
    {
      match: (p: ProductTypesInGroup) =>
        p instanceof FloorplanSimplePlaceholder && p.type === FloorplanPlaceholderType.CENTRAL_STATION,
      addFn: (p: FloorplanSimplePlaceholder, group?: Konva.Group) => this.addCentralStation(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanCentralStation, floorplanState.centralStationsVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.centralStationsItemTextVisible),
      ungroupedProducts: this.centralStationGroup,
      imageType: ImageType.CENTRAL_STATION_PLACEHOLDER_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanSimplePlaceholder && p.type === FloorplanPlaceholderType.TELEMETRY,
      addFn: (p: FloorplanSimplePlaceholder, group?: Konva.Group) => this.addTelemetry(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanTelemetry, floorplanState.telemetriesVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.telemetriesItemTextVisible),
      ungroupedProducts: this.telemetryGroup,
      imageType: ImageType.TELEMETRY_PLACEHOLDER_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanSimplePlaceholder && p.type === FloorplanPlaceholderType.KVM,
      addFn: (p: FloorplanSimplePlaceholder, group?: Konva.Group) => this.addKvm(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanKvm, floorplanState.kvmsVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.kvmsItemTextVisible),
      ungroupedProducts: this.kvmGroup,
      imageType: ImageType.KVM_PLACEHOLDER_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanSimplePlaceholder && p.type === FloorplanPlaceholderType.DISPLAY,
      addFn: (p: FloorplanSimplePlaceholder, group?: Konva.Group) => this.addDisplay(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanDisplay, floorplanState.displaysVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.displaysItemTextVisible),
      ungroupedProducts: this.displayGroup,
      imageType: ImageType.DISPLAY_PLACEHOLDER_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanSimplePlaceholder && p.type === FloorplanPlaceholderType.UTILITY,
      addFn: (p: FloorplanSimplePlaceholder, group?: Konva.Group) => this.addUtility(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanUtility, floorplanState.utilitiesVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.utilitiesItemTextVisible),
      ungroupedProducts: this.utilityGroup,
      imageType: ImageType.UTILITY_PLACEHOLDER_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanFlameDetector,
      addFn: (p: FloorplanFlameDetector, group?: Konva.Group) => this.addFlameDetector(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanFlameDetector, floorplanState.flameDetectorsVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.flameDetectorsItemTextVisible),
      ungroupedProducts: this.flameDetectorGroup,
      imageType: ImageType.FLAME_DETECTOR_ICON,
    },
    {
      match: (p: ProductTypesInGroup) => p instanceof FloorplanFlameDetectorPlaceholder,
      addFn: (p: FloorplanFlameDetectorPlaceholder, group?: Konva.Group) => this.addFlameDetector(p, true, group),
      setVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setGroupVisibility(group, FloorplanFlameDetector, floorplanState.flameDetectorsVisible),
      setItemTextVisibility: (group: Konva.Group, floorplanState: FloorplanState) =>
        this.setProductGroupItemTextVisibility(group, floorplanState.flameDetectorsItemTextVisible),
      ungroupedProducts: this.flameDetectorGroup,
      imageType: ImageType.FLAME_DETECTOR_PLACEHOLDER_ICON,
    },
  ];

  init(floorplan: Floorplan, container: HTMLDivElement): Observable<void> {
    Konva.hitOnDragEnabled = true;

    const floorplanObservable = new Observable<Konva.Shape>((observer) => {
      if (floorplan.fileUrl) {
        Konva.Image.fromURL(floorplan.fileUrl, (floorplanImage: Konva.Image) => {
          observer.next(floorplanImage);
          observer.complete();
        });
      } else {
        const emptyFloorplan = FloorplanKonva.initEmptyPlan();
        observer.next(emptyFloorplan);
        observer.complete();
      }
    });

    return floorplanObservable.pipe(
      map((floorplanShape: Konva.Shape) => this.initFloorplan(floorplan, floorplanShape, container)),
      switchMap(() =>
        ImageFactoryKonva.create(
          this.productService,
          this.monitoringProductService,
          this.monitoringProductImageService,
          this.utilityProductService,
        ),
      ),
      map((imageFactory) => {
        this.imageFactory = imageFactory;
      }),
      switchMap(() => of(undefined)),
    );
  }

  ngOnDestroy(): void {
    this._selectedItemChanged$.complete();
    this.stage.destroy();
    this.imageFactory?.destroy();
    if (this.selectionRectMouseMoveHandler) window.removeEventListener("mousemove", this.selectionRectMouseMoveHandler);
    if (this.selectionRectMouseUpHandler) window.removeEventListener("mouseup", this.selectionRectMouseUpHandler);
  }

  destroy() {
    this.ngOnDestroy();
  }

  getBorderDimensions(): { width: number; height: number } {
    return { width: this.floorplanBorderShape.width(), height: this.floorplanBorderShape.height() };
  }

  get selectedItemChanged$(): Observable<{ item: FloorplanItem | null; selectIntention: ItemSelectIntention | null }> {
    return this._selectedItemChanged$.asObservable();
  }

  get selectedItemsChanged$(): Observable<FloorplanItem[]> {
    return this._selectedItemsChanged$.asObservable();
  }

  setLockState(floorplanItem: FloorplanItem) {
    const node = this.nodesByFloorplanItem.get(floorplanItem);
    if (node) {
      this.setNodeLockState(node, floorplanItem.locked);
    }
  }

  setItemTextVisibility(floorplanItem: { floorplanItem: FloorplanItem; visible: boolean }) {
    const node = this.nodesByFloorplanItem.get(floorplanItem.floorplanItem);
    if (node) {
      const itemText = (node as Konva.Group).findOne(".itemText") as Konva.Group | null;
      if (!itemText) return;
      itemText.visible(floorplanItem.visible);
      itemText.setAttr("labelHidden", !floorplanItem.visible);

      const groupChip = (node as Konva.Group).findOne(".groupChip");
      if (groupChip) groupChip.visible(floorplanItem.visible);

      if (!floorplanItem.visible) return;
      if (groupChip) {
        IconKonva.refreshItemText(floorplanItem.floorplanItem, node as Konva.Group);
        return;
      }
      FloorplanItemTextKonva.updateBackground(itemText);
    }
  }

  setGlobalItemTextVisibilityState(floorplanState: FloorplanState) {
    this.setGroupItemTextVisibility(this.exZoneGroup, floorplanState.exZonesItemTextVisible);
    this.setGroupItemTextVisibility(this.dustExZoneGroup, floorplanState.dustExZonesItemTextVisible);
    this.setGroupItemTextVisibility(this.wildcardZoneGroup, floorplanState.wildcardZonesItemTextVisible);
    this.setGroupItemTextVisibility(this.imageGroup, floorplanState.imagesItemTextVisible);
    this.setGroupItemTextVisibility(this.floorplanTextGroup, floorplanState.floorplanTextsItemTextVisible);
    this.setGroupItemTextVisibility(this.freeTextGroup, floorplanState.floorplanFreeTextsItemTextVisible);
    this.setGroupItemTextVisibility(this.dangerAreaGroup, floorplanState.dangerAreasItemTextVisible);
    this.setGroupItemTextVisibility(this.measurementLineGroup, floorplanState.measurementLinesItemTextVisible);
    this.setGroupItemTextVisibility(this.referenceMeasure, floorplanState.referenceMeasureItemTextVisible);
    this.setGroupItemTextVisibility(this.airPathGroup, floorplanState.airPathsItemTextVisible);
    this.setGroupItemTextVisibility(this.transmitterGroup, floorplanState.transmittersItemTextVisible);
    this.setGroupItemTextVisibility(this.alarmDeviceGroup, floorplanState.alarmDevicesItemTextVisible);
    this.setGroupItemTextVisibility(this.gasWarningCenterGroup, floorplanState.gasWarningCentersItemTextVisible);
    this.setGroupItemTextVisibility(this.signalElementGroup, floorplanState.signalElementsItemTextVisible);
    this.setGroupItemTextVisibility(this.plasticSignGroup, floorplanState.plasticSignsItemTextVisible);
    this.setGroupItemTextVisibility(this.pipelineGroup, floorplanState.pipelinesItemTextVisible);
    this.setGroupItemTextVisibility(this.cableRouteGroup, floorplanState.cableRoutesItemTextVisible);
    this.setGroupItemTextVisibility(this.protectiveTubeGroup, floorplanState.protectiveTubesItemTextVisible);
    this.setGroupItemTextVisibility(this.wireGroup, floorplanState.wiresItemTextVisible);
    this.setGroupItemTextVisibility(this.patientMonitorGroup, floorplanState.patientMonitorsItemTextVisible);
    this.setGroupItemTextVisibility(this.centralStationGroup, floorplanState.centralStationsItemTextVisible);
    this.setGroupItemTextVisibility(this.telemetryGroup, floorplanState.telemetriesItemTextVisible);
    this.setGroupItemTextVisibility(this.kvmGroup, floorplanState.kvmsItemTextVisible);
    this.setGroupItemTextVisibility(this.displayGroup, floorplanState.displaysItemTextVisible);
    this.setGroupItemTextVisibility(this.utilityGroup, floorplanState.utilitiesItemTextVisible);
    this.productGroups
      .getChildren()
      .forEach((group) => this.setProductGroupsItemTextVisibility(group as Konva.Group, floorplanState));
  }

  updateAirPathsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.airPathGroup, visible);
  }

  updateAlarmDevicesItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.alarmDeviceGroup, visible);
    this.productGroups.getChildren().forEach((group) => {
      if (group instanceof Konva.Group) {
        group.getChildren().forEach((product: Konva.Node) => {
          const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
          if (
            floorplanItem instanceof FloorplanAlarmDevice ||
            (floorplanItem instanceof FloorplanSimplePlaceholder && floorplanItem.type === FloorplanPlaceholderType.ALARM_DEVICE)
          ) {
            this.setProductGroupItemTextVisibility(product as Konva.Group, visible);
          }
        });
      }
    });
  }

  updateGasWarningCentersItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.gasWarningCenterGroup, visible);
    this.productGroups.getChildren().forEach((group) => {
      if (group instanceof Konva.Group) {
        group.getChildren().forEach((product: Konva.Node) => {
          const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
          if (
            floorplanItem instanceof FloorplanGasWarningCenter ||
            floorplanItem instanceof FloorplanGasWarningCenterPlaceholder
          ) {
            this.setProductGroupItemTextVisibility(product as Konva.Group, visible);
          }
        });
      }
    });
  }

  updatePlasticSignsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.plasticSignGroup, visible);
    this.productGroups.getChildren().forEach((group) => {
      if (group instanceof Konva.Group) {
        group.getChildren().forEach((product: Konva.Node) => {
          const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
          if (
            floorplanItem instanceof FloorplanPlasticSign ||
            (floorplanItem instanceof FloorplanSimplePlaceholder && floorplanItem.type === FloorplanPlaceholderType.PLASTIC_SIGN)
          ) {
            this.setProductGroupItemTextVisibility(product as Konva.Group, visible);
          }
        });
      }
    });
  }

  updateSignalElementsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.signalElementGroup, visible);
    this.productGroups.getChildren().forEach((group) => {
      if (group instanceof Konva.Group) {
        group.getChildren().forEach((product: Konva.Node) => {
          const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
          if (
            floorplanItem instanceof FloorplanSignalElement ||
            (floorplanItem instanceof FloorplanSimplePlaceholder &&
              floorplanItem.type === FloorplanPlaceholderType.SIGNAL_ELEMENT)
          ) {
            this.setProductGroupItemTextVisibility(product as Konva.Group, visible);
          }
        });
      }
    });
  }

  updateTransmittersItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.transmitterGroup, visible);
    this.productGroups.getChildren().forEach((group) => {
      if (group instanceof Konva.Group) {
        group.getChildren().forEach((product: Konva.Node) => {
          const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
          if (floorplanItem instanceof FloorplanTransmitter || floorplanItem instanceof FloorplanTransmitterPlaceholder) {
            this.setProductGroupItemTextVisibility(product as Konva.Group, visible);
          }
        });
      }
    });
  }

  updateFlameDetectorsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.flameDetectorGroup, visible);
    this.productGroups.getChildren().forEach((group) => {
      if (group instanceof Konva.Group) {
        group.getChildren().forEach((product: Konva.Node) => {
          const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
          if (floorplanItem instanceof FloorplanFlameDetector || floorplanItem instanceof FloorplanFlameDetectorPlaceholder) {
            this.setProductGroupItemTextVisibility(product as Konva.Group, visible);
          }
        });
      }
    });
  }

  updateImagesItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.imageGroup, visible);
  }

  updateMeasurementLinesItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.measurementLineGroup, visible);
  }

  updateReferenceMeasureItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.referenceMeasure, visible);
  }

  updatePipelinesItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.pipelineGroup, visible);
  }

  updateProtectiveTubesItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.protectiveTubeGroup, visible);
  }

  updateTextsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.floorplanTextGroup, visible);
  }

  updateFreeTextsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.freeTextGroup, visible);
  }

  updateWildcardZonesItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.wildcardZoneGroup, visible);
  }

  updateWiresItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.wireGroup, visible);
  }

  updateCableRoutesItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.cableRouteGroup, visible);
  }

  updateDangerAreasItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.dangerAreaGroup, visible);
  }

  updateDustExZonesItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.dustExZoneGroup, visible);
  }

  updateExZonesItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.exZoneGroup, visible);
  }

  updateIsolationRoomsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.isolationRoomGroup, visible);
  }

  updateItRoomsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.itRoomGroup, visible);
  }

  updateOperatingTheatresItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.operatingTheatreGroup, visible);
  }

  updateProcedureRoomsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.procedureRoomGroup, visible);
  }

  updateStandardRoomsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.standardRoomGroup, visible);
  }

  updatePatientMonitorsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.patientMonitorGroup, visible);
    this.productGroups.getChildren().forEach((group) => {
      if (group instanceof Konva.Group) {
        group.getChildren().forEach((product: Konva.Node) => {
          const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
          if (
            floorplanItem instanceof FloorplanPatientMonitor ||
            (floorplanItem instanceof FloorplanSimplePlaceholder &&
              floorplanItem.type === FloorplanPlaceholderType.PATIENT_MONITOR)
          ) {
            this.setProductGroupItemTextVisibility(product as Konva.Group, visible);
          }
        });
      }
    });
  }

  updateCentralStationsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.centralStationGroup, visible);
    this.productGroups.getChildren().forEach((group) => {
      if (group instanceof Konva.Group) {
        group.getChildren().forEach((product: Konva.Node) => {
          const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
          if (
            floorplanItem instanceof FloorplanCentralStation ||
            (floorplanItem instanceof FloorplanSimplePlaceholder &&
              floorplanItem.type === FloorplanPlaceholderType.CENTRAL_STATION)
          ) {
            this.setProductGroupItemTextVisibility(product as Konva.Group, visible);
          }
        });
      }
    });
  }

  updateTelemetriesItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.telemetryGroup, visible);
    this.productGroups.getChildren().forEach((group) => {
      if (group instanceof Konva.Group) {
        group.getChildren().forEach((product: Konva.Node) => {
          const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
          if (
            floorplanItem instanceof FloorplanTelemetry ||
            (floorplanItem instanceof FloorplanSimplePlaceholder && floorplanItem.type === FloorplanPlaceholderType.TELEMETRY)
          ) {
            const itemText = (product as Konva.Group).findOne(".itemText");
            const groupChip = (product as Konva.Group).findOne(".groupChip");
            if (!itemText || !groupChip) return;

            itemText.visible(visible);
            groupChip.visible(visible);
          }
        });
      }
    });
  }

  updateKvmsItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.kvmGroup, visible);
    this.productGroups.getChildren().forEach((group) => {
      if (group instanceof Konva.Group) {
        group.getChildren().forEach((product: Konva.Node) => {
          const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
          if (
            floorplanItem instanceof FloorplanKvm ||
            (floorplanItem instanceof FloorplanSimplePlaceholder && floorplanItem.type === FloorplanPlaceholderType.KVM)
          ) {
            const itemText = (product as Konva.Group).findOne(".itemText");
            const groupChip = (product as Konva.Group).findOne(".groupChip");
            if (!itemText || !groupChip) return;

            itemText.visible(visible);
            groupChip.visible(visible);
          }
        });
      }
    });
  }

  updateDisplaysItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.displayGroup, visible);
    this.productGroups.getChildren().forEach((group) => {
      if (group instanceof Konva.Group) {
        group.getChildren().forEach((product: Konva.Node) => {
          const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
          if (
            floorplanItem instanceof FloorplanDisplay ||
            (floorplanItem instanceof FloorplanSimplePlaceholder && floorplanItem.type === FloorplanPlaceholderType.DISPLAY)
          ) {
            const itemText = (product as Konva.Group).findOne(".itemText");
            const groupChip = (product as Konva.Group).findOne(".groupChip");
            if (!itemText || !groupChip) return;

            itemText.visible(visible);
            groupChip.visible(visible);
          }
        });
      }
    });
  }

  updateUtilitiesItemTextVisibility(visible: boolean) {
    this.setGroupItemTextVisibility(this.utilityGroup, visible);
    this.productGroups.getChildren().forEach((group) => {
      if (group instanceof Konva.Group) {
        group.getChildren().forEach((product: Konva.Node) => {
          const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
          if (
            floorplanItem instanceof FloorplanUtility ||
            (floorplanItem instanceof FloorplanSimplePlaceholder && floorplanItem.type === FloorplanPlaceholderType.UTILITY)
          ) {
            const itemText = (product as Konva.Group).findOne(".itemText");
            const groupChip = (product as Konva.Group).findOne(".groupChip");
            if (!itemText || !groupChip) return;

            itemText.visible(visible);
            groupChip.visible(visible);
          }
        });
      }
    });
  }

  setVisibilityState(floorplanState: FloorplanState) {
    this.monitoringAreaVisible = floorplanState.monitoringAreasVisible;
    this.coverageAreaConeVisible = floorplanState.coverageAreasVisible && floorplanState.flameDetectorsVisible;
    this.setGroupVisibility(this.exZoneGroup, ExZone, floorplanState.exZonesVisible);
    this.setGroupVisibility(this.dustExZoneGroup, DustExZone, floorplanState.dustExZonesVisible);
    this.setGroupVisibility(this.wildcardZoneGroup, WildcardZone, floorplanState.wildcardZonesVisible);
    this.setGroupVisibility(this.imageGroup, FloorplanImage, floorplanState.imagesVisible);
    this.setGroupVisibility(this.floorplanTextGroup, FloorplanText, floorplanState.floorplanTextsVisible);
    this.setGroupVisibility(this.freeTextGroup, FloorplanFreeText, floorplanState.floorplanFreeTextsVisible);
    this.setGroupVisibility(this.dangerAreaGroup, DangerArea, floorplanState.dangerAreasVisible);
    this.setGroupVisibility(this.measurementLineGroup, MeasurementLine, floorplanState.measurementLinesVisible);
    this.setGroupVisibility(this.referenceMeasure, ReferenceMeasure, floorplanState.referenceMeasureVisible);
    this.setGroupVisibility(this.airPathGroup, AirPath, floorplanState.airPathsVisible);
    this.setGroupVisibility(this.transmitterGroup, FloorplanTransmitter, floorplanState.transmittersVisible);
    this.setGroupVisibility(this.alarmDeviceGroup, FloorplanAlarmDevice, floorplanState.alarmDevicesVisible);
    this.setGroupVisibility(this.gasWarningCenterGroup, FloorplanGasWarningCenter, floorplanState.gasWarningCentersVisible);
    this.setGroupVisibility(this.signalElementGroup, FloorplanSignalElement, floorplanState.signalElementsVisible);
    this.setGroupVisibility(this.plasticSignGroup, FloorplanPlasticSign, floorplanState.plasticSignsVisible);
    this.setGroupVisibility(this.pipelineGroup, Pipeline, floorplanState.pipelinesVisible);
    this.setGroupVisibility(this.cableRouteGroup, CableRoute, floorplanState.cableRoutesVisible);
    this.setGroupVisibility(this.protectiveTubeGroup, ProtectiveTube, floorplanState.protectiveTubesVisible);
    this.setGroupVisibility(this.wireGroup, Wire, floorplanState.wiresVisible);
    this.setGroupVisibility(this.isolationRoomGroup, IsolationRoom, floorplanState.isolationRoomsVisible);
    this.setGroupVisibility(this.itRoomGroup, ItRoom, floorplanState.itRoomsVisible);
    this.setGroupVisibility(this.operatingTheatreGroup, OperatingTheatre, floorplanState.operatingTheatresVisible);
    this.setGroupVisibility(this.procedureRoomGroup, ProcedureRoom, floorplanState.procedureRoomsVisible);
    this.setGroupVisibility(this.standardRoomGroup, StandardRoom, floorplanState.standardRoomsVisible);
    this.setGroupVisibility(this.patientMonitorGroup, FloorplanPatientMonitor, floorplanState.patientMonitorsVisible);
    this.setGroupVisibility(this.centralStationGroup, FloorplanCentralStation, floorplanState.centralStationsVisible);
    this.setGroupVisibility(this.telemetryGroup, FloorplanTelemetry, floorplanState.telemetriesVisible);
    this.setGroupVisibility(this.kvmGroup, FloorplanKvm, floorplanState.kvmsVisible);
    this.setGroupVisibility(this.displayGroup, FloorplanDisplay, floorplanState.displaysVisible);
    this.setGroupVisibility(this.utilityGroup, FloorplanUtility, floorplanState.utilitiesVisible);
    this.setGroupVisibility(this.flameDetectorGroup, FloorplanFlameDetector, floorplanState.flameDetectorsVisible);
    this.productGroups.getChildren().forEach((group) => this.setProductGroupVisibility(group as Konva.Group, floorplanState));
    this.setMonitoringAreaVisibility(this.monitoringAreaVisible);
    this.setCoverageAreaConeVisibility(this.coverageAreaConeVisible);
  }

  setProductGroupVisibility(group: Konva.Group, floorplanState: FloorplanState) {
    group.getChildren().forEach((product: Konva.Node) => {
      const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;

      if (
        product instanceof Konva.Group &&
        (floorplanItem instanceof FloorplanGasWarningCenter ||
          floorplanItem instanceof FloorplanGasWarningCenterPlaceholder ||
          floorplanItem instanceof FloorplanAlarmDevice ||
          floorplanItem instanceof FloorplanSignalElement ||
          floorplanItem instanceof FloorplanPlasticSign ||
          floorplanItem instanceof FloorplanTransmitter ||
          floorplanItem instanceof FloorplanTransmitterPlaceholder ||
          floorplanItem instanceof FloorplanFlameDetector ||
          floorplanItem instanceof FloorplanFlameDetectorPlaceholder ||
          floorplanItem instanceof FloorplanPatientMonitor ||
          floorplanItem instanceof FloorplanCentralStation ||
          floorplanItem instanceof FloorplanTelemetry ||
          floorplanItem instanceof FloorplanUtility ||
          floorplanItem instanceof FloorplanKvm ||
          floorplanItem instanceof FloorplanDisplay ||
          floorplanItem instanceof FloorplanSimplePlaceholder)
      ) {
        for (const handler of this.groupHandlers) {
          if (handler.match(floorplanItem)) {
            handler.setVisibility(product, floorplanState);
            break;
          }
        }
      }
    });
  }

  setProductGroupsItemTextVisibility(group: Konva.Group, floorplanState: FloorplanState) {
    group.getChildren().forEach((product: Konva.Node) => {
      const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;

      if (
        product instanceof Konva.Group &&
        (floorplanItem instanceof FloorplanGasWarningCenter ||
          floorplanItem instanceof FloorplanGasWarningCenterPlaceholder ||
          floorplanItem instanceof FloorplanAlarmDevice ||
          floorplanItem instanceof FloorplanSignalElement ||
          floorplanItem instanceof FloorplanPlasticSign ||
          floorplanItem instanceof FloorplanTransmitter ||
          floorplanItem instanceof FloorplanTransmitterPlaceholder ||
          floorplanItem instanceof FloorplanFlameDetector ||
          floorplanItem instanceof FloorplanFlameDetectorPlaceholder ||
          floorplanItem instanceof FloorplanPatientMonitor ||
          floorplanItem instanceof FloorplanCentralStation ||
          floorplanItem instanceof FloorplanTelemetry ||
          floorplanItem instanceof FloorplanUtility ||
          floorplanItem instanceof FloorplanKvm ||
          floorplanItem instanceof FloorplanDisplay ||
          floorplanItem instanceof FloorplanSimplePlaceholder)
      ) {
        for (const handler of this.groupHandlers) {
          if (handler.match(floorplanItem)) {
            handler.setItemTextVisibility(product, floorplanState);
            break;
          }
        }
      }
    });
  }

  setGroupMonitoringAreaVisibility(group: Konva.Group, visible: boolean) {
    group.getChildren().forEach((product: Konva.Node) => {
      const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
      if (floorplanItem instanceof FloorplanTransmitter || floorplanItem instanceof FloorplanTransmitterPlaceholder) {
        TransmitterKonva.setMonitoringAreaVisibility(product as Konva.Group, visible);
      }
    });
  }

  setGroupCoverageAreaConeVisibility(group: Konva.Group, visible: boolean) {
    group.getChildren().forEach((product: Konva.Node) => {
      const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
      if (floorplanItem instanceof FloorplanFlameDetector || floorplanItem instanceof FloorplanFlameDetectorPlaceholder) {
        FlameDetectorKonva.setCoverageAreaVisibility(product as Konva.Group, visible);
      }
    });
  }

  addExZone(exZone: ExZone, select: boolean) {
    const node = ExZoneKonva.init(exZone);
    const exZoneGroup = node.getChildren()[0] as Konva.Group;
    const itemText = node.getChildren()[1] as Konva.Group;
    const text = FloorplanItemTextKonva.getText(itemText);

    this.initKonvaElement(exZoneGroup, select);
    ZoneKonva.setTextPosition(itemText, exZoneGroup);
    ZoneKonva.scaleText(text, exZoneGroup);
    this.setProductGroupItemTextVisibility(node, exZone.itemTextVisible);
    exZoneGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(exZoneGroup); // reset transformers so that it fits to positionId
    });
    this.addItem(exZone, node, this.exZoneGroup);
  }

  addDustExZone(dustExZone: DustExZone, select: boolean) {
    const node = DustExZoneKonva.init(dustExZone);
    const dustExZoneGroup = node.getChildren()[0] as Konva.Group;
    const itemText = node.getChildren()[1] as Konva.Group;
    const text = FloorplanItemTextKonva.getText(itemText);

    this.initKonvaElement(dustExZoneGroup, select);
    ZoneKonva.setTextPosition(itemText, dustExZoneGroup);
    ZoneKonva.scaleText(text, dustExZoneGroup);
    this.setProductGroupItemTextVisibility(node, dustExZone.itemTextVisible);
    dustExZoneGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(dustExZoneGroup); // reset transformers so that it fits to positionId
    });
    this.addItem(dustExZone, node, this.dustExZoneGroup);
  }

  addWildcardZone(zone: WildcardZone, select: boolean) {
    const node = WildcardZoneKonva.init(zone);
    const zoneGroup = node.getChildren()[0] as Konva.Group;
    const itemText = node.getChildren()[1] as Konva.Group;
    const text = FloorplanItemTextKonva.getText(itemText);

    this.initKonvaElement(zoneGroup, select);
    ZoneKonva.setTextPosition(itemText, zoneGroup);
    ZoneKonva.scaleText(text, zoneGroup);
    this.setProductGroupItemTextVisibility(node, zone.itemTextVisible);
    zoneGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(zoneGroup); // reset transformers so that it fits to positionId
    });
    this.addItem(zone, node, this.wildcardZoneGroup);
  }

  addDangerArea(dangerArea: DangerArea, select: boolean): void {
    const node = DangerAreaKonva.init(dangerArea);
    const dangerAreaGroup = node.getChildren()[0] as Konva.Group;
    const itemText = node.getChildren()[1] as Konva.Group;
    const text = FloorplanItemTextKonva.getText(itemText);

    this.initKonvaElement(dangerAreaGroup, select);
    ZoneKonva.setTextPosition(itemText, dangerAreaGroup);
    ZoneKonva.scaleText(text, dangerAreaGroup);
    this.setProductGroupItemTextVisibility(node, dangerArea.itemTextVisible);
    dangerAreaGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(dangerAreaGroup); // reset transformers so that it fits to positionId
    });
    this.addItem(dangerArea, node, this.dangerAreaGroup);
  }

  addImage(floorplanImage: FloorplanImage, select: boolean): Observable<void> {
    return this.trackFloorplanImageLoad(
      new Observable((subscriber) => {
        const projectId = this.floorplan.project.id;

        if (!projectId) {
          subscriber.error(new Error("No projectId available"));
          return;
        }
        this.projectImageService.getPreviewImageFromIndexedDB(floorplanImage.projectImageId).subscribe({
          next: (blob: Blob | undefined) => {
            if (!blob) {
              subscriber.error(new Error("Image blob not found in IndexedDB for id: " + floorplanImage.projectImageId));
              return;
            }

            const url = URL.createObjectURL(blob);

            Konva.Image.fromURL(url, (img: any) => {
              URL.revokeObjectURL(url);

              if (!img) {
                subscriber.error(new Error("Failed to load image from URL"));
                return;
              }

              const node = ImageKonva.init(floorplanImage, img);
              const imgGroup = node.getChildren()[0] as Konva.Group;

              this.initKonvaElement(imgGroup, select);
              this.setProductGroupItemTextVisibility(node, floorplanImage.itemTextVisible);

              imgGroup.on("dragend", () => this.updateAfterDrag(imgGroup));
              this.addItem(floorplanImage, node, this.imageGroup);

              subscriber.next();
              subscriber.complete();
            });
          },
          error: (err) => {
            subscriber.error(new Error("Failed to retrieve image blob from IndexedDB: " + err));
          },
        });
      }),
    );
  }

  addMeasurementLine(measurementLine: MeasurementLine, select: boolean): void {
    const node = MeasurementLineKonva.init(measurementLine);
    const measurementLineGroup = node.getChildren()[0] as Konva.Group;
    this.initKonvaElement(measurementLineGroup, select);
    this.setProductGroupItemTextVisibility(node, measurementLine.itemTextVisible);

    measurementLineGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(measurementLineGroup); // reset transformers so that it fits to positionId
    });
    this.addItem(measurementLine, node, this.measurementLineGroup);
  }

  addReferenceMeasure(referenceMeasure: ReferenceMeasure, select: boolean): void {
    const node = ReferenceMeasureKonva.init(referenceMeasure);
    const referenceMeasureGroup = node.getChildren()[0] as Konva.Group;
    this.initKonvaElement(referenceMeasureGroup, select);
    this.setProductGroupItemTextVisibility(node, referenceMeasure.itemTextVisible);

    referenceMeasureGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(referenceMeasureGroup); // reset transformers so that it fits to positionId
    });
    this.addItem(referenceMeasure, node, this.referenceMeasure);
    this.autoScaleItems();
  }

  addText(text: FloorplanText, select: boolean): void {
    this.initIconElement(
      text,
      this.imageFactory.createImage(ImageType.TEXT_ICON),
      this.floorplanTextGroup,
      text.itemTextVisible,
      select,
    );
  }

  addFreeText(freeText: FloorplanFreeText, select: boolean): void {
    const node = FreeTextKonva.init(freeText);
    const freeTextGroup = node.getChildren()[0] as Konva.Group;
    freeTextGroup.setAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY, freeText);
    this.initKonvaElement(freeTextGroup, select);
    this.setNodeLockState(node, freeText.locked);
    this.addItem(freeText, node, this.freeTextGroup);
  }

  addAirPath(airPath: AirPath, select: boolean): void {
    const node = AirPathKonva.init(airPath) as Konva.Group;
    const airPathGroup = node.getChildren()[0] as Konva.Group;
    this.initKonvaElement(airPathGroup, select);
    this.setProductGroupItemTextVisibility(node, airPath.itemTextVisible);

    airPathGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(airPathGroup); // reset transformers so that it fits to positionId
    });
    this.airPathGroup.add(node);
    this.addItem(airPath, node, this.airPathGroup);
  }

  addPipeline(pipeline: Pipeline, select: boolean): void {
    const node = PipelineKonva.init(pipeline) as Konva.Group;
    const pipelineGroup = node.getChildren()[0] as Konva.Group;
    this.initKonvaElement(pipelineGroup, select);
    this.setProductGroupItemTextVisibility(node, pipeline.itemTextVisible);

    pipelineGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(pipelineGroup); // reset transformers so that it fits to positionId
    });
    this.pipelineGroup.add(node);
    this.addItem(pipeline, node, this.pipelineGroup);
  }

  addCableRoute(cableRoute: CableRoute, select: boolean): void {
    const node = CableRouteKonva.init(cableRoute);
    const cableRouteGroup = node.getChildren()[0] as Konva.Group;

    this.initKonvaElement(cableRouteGroup, select);
    this.setProductGroupItemTextVisibility(node, cableRoute.itemTextVisible);

    cableRouteGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(cableRouteGroup); // reset transformers so that it fits to positionId
    });
    this.cableRouteGroup.add(node);
    this.addItem(cableRoute, node, this.cableRouteGroup);
  }

  addProtectiveTube(protectiveTube: ProtectiveTube, select: boolean): void {
    const node = ProtectiveTubeKonva.init(protectiveTube);
    const protectiveTubeGroup = node.getChildren()[0] as Konva.Group;

    this.initKonvaElement(protectiveTubeGroup, select);
    this.setProductGroupItemTextVisibility(node, protectiveTube.itemTextVisible);

    protectiveTubeGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(protectiveTubeGroup); // reset transformers so that it fits to positionId
    });
    this.protectiveTubeGroup.add(node);
    this.addItem(protectiveTube, node, this.protectiveTubeGroup);
  }

  addWire(wire: Wire, select: boolean): void {
    const node = WireKonva.init(wire);
    const wireGroup = node.getChildren()[0] as Konva.Group;

    this.initKonvaElement(wireGroup, select);
    this.setProductGroupItemTextVisibility(node, wire.itemTextVisible);

    wireGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(wireGroup); // reset transformers so that it fits to positionId
    });
    this.wireGroup.add(node);
    this.addItem(wire, node, this.wireGroup);
  }

  addTransmitter(
    transmitter: FloorplanTransmitter | FloorplanTransmitterPlaceholder,
    select: boolean,
    groupNode?: Konva.Group,
  ): void {
    let transmitterIcon;
    const warningSignIcon = this.imageFactory.createImage(ImageType.WARNING_SIGN_ICON);

    if (
      this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC ||
      transmitter instanceof FloorplanTransmitterPlaceholder
    ) {
      transmitterIcon = this.imageFactory.createImage(
        transmitter instanceof FloorplanTransmitter ? ImageType.TRANSMITTER_ICON : ImageType.TRANSMITTER_PLACEHOLDER_ICON,
      );
      this.handleAddTransmitter(
        transmitter,
        transmitterIcon,
        warningSignIcon,
        select,
        this.resolveProductGroupNode(transmitter, groupNode),
      );
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      const image$ = this.trackProductImageLoad(this.imageFactory.createProductImage$(transmitter));
      image$.subscribe((transmitterIcon) => {
        this.handleAddTransmitter(
          transmitter,
          transmitterIcon,
          warningSignIcon,
          select,
          this.resolveProductGroupNode(transmitter, groupNode),
        );
      });
    }
  }

  addFlameDetector(
    flameDetector: FloorplanFlameDetector | FloorplanFlameDetectorPlaceholder,
    select: boolean,
    groupNode?: Konva.Group,
  ): void {
    let icon;
    const warningSignIcon = this.imageFactory.createImage(ImageType.WARNING_SIGN_ICON);
    const resolvedGroupNode = this.resolveProductGroupNode(flameDetector, groupNode);
    if (
      this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC ||
      flameDetector instanceof FloorplanFlameDetectorPlaceholder
    ) {
      icon = this.imageFactory.createImage(
        flameDetector instanceof FloorplanFlameDetector
          ? ImageType.FLAME_DETECTOR_ICON
          : ImageType.FLAME_DETECTOR_PLACEHOLDER_ICON,
      );
      this.handleAddFlameDetector(flameDetector, icon, warningSignIcon, select, resolvedGroupNode);
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      const image$ = this.trackProductImageLoad(this.imageFactory.createProductImage$(flameDetector));
      image$.subscribe((icon) => {
        const resolvedGroupNode = this.resolveProductGroupNode(flameDetector, groupNode);
        this.handleAddFlameDetector(flameDetector, icon, warningSignIcon, select, resolvedGroupNode);
      });
    }
  }

  private handleAddFlameDetector(
    flameDetector: FloorplanFlameDetector | FloorplanFlameDetectorPlaceholder,
    flameDetectorIcon: Konva.Image,
    warningSignIcon: Konva.Image,
    select: boolean,
    groupNode?: Konva.Group,
  ) {
    const productName = flameDetector instanceof FloorplanFlameDetector ? flameDetector.config.name : "";
    const fieldOfView = CoverageAreaConeKonva.getFieldOfView(productName);
    const shapes = FlameDetectorKonva.init(flameDetector, flameDetectorIcon, warningSignIcon, fieldOfView, groupNode);
    this.initKonvaElement(shapes.flameDetector, select);
    if (shapes.coverageAreaCone) {
      this.initKonvaElement(shapes.coverageAreaCone);
      this.initConeRotationHandler(shapes.coverageAreaCone, flameDetector, fieldOfView);
      this.flameDetectorConeGroup.add(shapes.coverageAreaCone);
    }
    this.setProductGroupItemTextVisibility(shapes.flameDetectorGroup, flameDetector.itemTextVisible);

    if (groupNode) {
      this.productGroups.add(groupNode);
      this.addItem(flameDetector, shapes.flameDetectorGroup, groupNode);
      groupNode.getLayer()?.batchDraw();
    } else {
      this.addItem(flameDetector, shapes.flameDetectorGroup, this.flameDetectorGroup);
    }

    FlameDetectorKonva.setCoverageAreaVisibility(shapes.flameDetectorGroup, this.coverageAreaConeVisible);
  }

  private initConeRotationHandler(
    cone: Konva.Node,
    flameDetector: FloorplanFlameDetector | FloorplanFlameDetectorPlaceholder,
    fieldOfViewDeg: number,
  ) {
    cone.on("transform", () => {
      cone.scaleX(1);
      cone.scaleY(1);
    });
    cone.on("transformend", () => {
      const normalizedRotation = (((cone.rotation() + fieldOfViewDeg / 2) % 360) + 360) % 360;
      flameDetector.coneRotation = normalizedRotation;
    });
  }

  private handleAddTransmitter(
    transmitter: FloorplanTransmitter | FloorplanTransmitterPlaceholder,
    transmitterIcon: any,
    warningSignIcon: any,
    select: boolean,
    groupNode?: Konva.Group,
  ) {
    const transmitterShapes = TransmitterKonva.init(transmitter, transmitterIcon, warningSignIcon, groupNode);
    this.initKonvaElement(transmitterShapes.transmitter, select);
    this.initKonvaElement(transmitterShapes.monitoringArea);
    this.setProductGroupItemTextVisibility(transmitterShapes.transmitterGroup, transmitter.itemTextVisible);

    if (groupNode) {
      this.productGroups.add(groupNode);
      this.addItem(transmitter, transmitterShapes.transmitterGroup, groupNode);
      groupNode.getLayer()?.batchDraw();
    } else {
      this.addItem(transmitter, transmitterShapes.transmitterGroup, this.transmitterGroup);
    }

    if (transmitter.remoteSensor) {
      this.attachRemoteSensor(transmitter, false);
    }
    TransmitterKonva.setMonitoringAreaVisibility(transmitterShapes.transmitterGroup, this.monitoringAreaVisible);
  }

  addAlarmDevice(alarmDevice: FloorplanAlarmDevice | FloorplanSimplePlaceholder, select: boolean, groupNode?: Konva.Group): void {
    let icon;
    const resolvedGroupNode = this.resolveProductGroupNode(alarmDevice, groupNode);
    if (
      this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC ||
      alarmDevice instanceof FloorplanSimplePlaceholder
    ) {
      icon = this.imageFactory.createImage(
        alarmDevice instanceof FloorplanAlarmDevice ? ImageType.ALARM_DEVICE_ICON : ImageType.ALARM_DEVICE_PLACEHOLDER_ICON,
      );
      if (resolvedGroupNode) {
        this.initGroupIconElement(alarmDevice, icon, resolvedGroupNode, alarmDevice.itemTextVisible, select);
      } else {
        this.initIconElement(alarmDevice, icon, this.alarmDeviceGroup, alarmDevice.itemTextVisible, select);
      }
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      const image$ = this.trackProductImageLoad(this.imageFactory.createProductImage$(alarmDevice));
      image$.subscribe((icon) => {
        const resolvedGroupNode = this.resolveProductGroupNode(alarmDevice, groupNode);
        if (resolvedGroupNode) {
          this.initGroupIconElement(alarmDevice, icon, resolvedGroupNode, alarmDevice.itemTextVisible, select);
        } else {
          this.initIconElement(alarmDevice, icon, this.alarmDeviceGroup, alarmDevice.itemTextVisible, select);
        }
      });
    }
  }

  addGasWarningCenter(
    gasWarningCenter: FloorplanGasWarningCenterPlaceholder | FloorplanGasWarningCenter,
    select: boolean,
    groupNode?: Konva.Group,
  ): void {
    let icon;
    const resolvedGroupNode = this.resolveProductGroupNode(gasWarningCenter, groupNode);
    if (
      this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC ||
      gasWarningCenter instanceof FloorplanGasWarningCenterPlaceholder
    ) {
      icon = this.imageFactory.createImage(
        gasWarningCenter instanceof FloorplanGasWarningCenter
          ? ImageType.GAS_WARNING_CENTER_ICON
          : ImageType.GAS_WARNING_CENTER_PLACEHOLDER_ICON,
      );
      if (resolvedGroupNode) {
        this.initGroupIconElement(gasWarningCenter, icon, resolvedGroupNode, gasWarningCenter.itemTextVisible, select);
      } else {
        this.initIconElement(gasWarningCenter, icon, this.gasWarningCenterGroup, gasWarningCenter.itemTextVisible, select);
      }
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      const image$ = this.trackProductImageLoad(this.imageFactory.createProductImage$(gasWarningCenter));
      image$.subscribe((icon) => {
        const resolvedGroupNode = this.resolveProductGroupNode(gasWarningCenter, groupNode);
        if (resolvedGroupNode) {
          this.initGroupIconElement(gasWarningCenter, icon, resolvedGroupNode, gasWarningCenter.itemTextVisible, select);
        } else {
          this.initIconElement(gasWarningCenter, icon, this.gasWarningCenterGroup, gasWarningCenter.itemTextVisible, select);
        }
      });
    }
  }

  addSignalElement(
    signalElement: FloorplanSimplePlaceholder | FloorplanSignalElement,
    select: boolean,
    groupNode?: Konva.Group,
  ): void {
    let icon;
    const resolvedGroupNode = this.resolveProductGroupNode(signalElement, groupNode);
    if (
      this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC ||
      signalElement instanceof FloorplanSimplePlaceholder
    ) {
      icon = this.imageFactory.createImage(
        signalElement instanceof FloorplanSignalElement
          ? ImageType.SIGNAL_ELEMENT_ICON
          : ImageType.SIGNAL_ELEMENT_PLACEHOLDER_ICON,
      );
      if (resolvedGroupNode) {
        this.initGroupIconElement(signalElement, icon, resolvedGroupNode, signalElement.itemTextVisible, select);
      } else {
        this.initIconElement(signalElement, icon, this.signalElementGroup, signalElement.itemTextVisible, select);
      }
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      const image$ = this.trackProductImageLoad(this.imageFactory.createProductImage$(signalElement));
      image$.subscribe((icon) => {
        const resolvedGroupNode = this.resolveProductGroupNode(signalElement, groupNode);
        if (resolvedGroupNode) {
          this.initGroupIconElement(signalElement, icon, resolvedGroupNode, signalElement.itemTextVisible, select);
        } else {
          this.initIconElement(signalElement, icon, this.signalElementGroup, signalElement.itemTextVisible, select);
        }
      });
    }
  }

  addPlasticSign(plasticSign: FloorplanSimplePlaceholder | FloorplanPlasticSign, select: boolean, groupNode?: Konva.Group): void {
    let icon;
    const resolvedGroupNode = this.resolveProductGroupNode(plasticSign, groupNode);
    if (
      this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC ||
      plasticSign instanceof FloorplanSimplePlaceholder
    ) {
      icon = this.imageFactory.createImage(
        plasticSign instanceof FloorplanPlasticSign ? ImageType.PLASTIC_SIGN_ICON : ImageType.PLASTIC_SIGN_PLACEHOLDER_ICON,
      );
      if (resolvedGroupNode) {
        this.initGroupIconElement(plasticSign, icon, resolvedGroupNode, plasticSign.itemTextVisible, select);
      } else {
        this.initIconElement(plasticSign, icon, this.plasticSignGroup, plasticSign.itemTextVisible, select);
      }
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      const image$ = this.trackProductImageLoad(this.imageFactory.createProductImage$(plasticSign));
      image$.subscribe((icon) => {
        const resolvedGroupNode = this.resolveProductGroupNode(plasticSign, groupNode);
        if (resolvedGroupNode) {
          this.initGroupIconElement(plasticSign, icon, resolvedGroupNode, plasticSign.itemTextVisible, select);
        } else {
          this.initIconElement(plasticSign, icon, this.plasticSignGroup, plasticSign.itemTextVisible, select);
        }
      });
    }
  }

  addPatientMonitor(
    patientMonitor: FloorplanSimplePlaceholder | FloorplanPatientMonitor,
    select: boolean,
    groupNode?: Konva.Group,
  ): void {
    let icon;
    const resolvedGroupNode = this.resolveProductGroupNode(patientMonitor, groupNode);
    if (
      this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC ||
      patientMonitor instanceof FloorplanSimplePlaceholder
    ) {
      icon = this.imageFactory.createImage(
        patientMonitor instanceof FloorplanPatientMonitor
          ? ImageType.PATIENT_MONITOR_ICON
          : ImageType.PATIENT_MONITOR_PLACEHOLDER_ICON,
      );
      if (resolvedGroupNode) {
        this.initGroupIconElement(patientMonitor, icon, resolvedGroupNode, patientMonitor.itemTextVisible, select);
      } else {
        this.initIconElement(patientMonitor, icon, this.patientMonitorGroup, patientMonitor.itemTextVisible, select);
      }
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      this.imageFactory.createProductImage$(patientMonitor).subscribe((icon) => {
        const resolvedGroupNode = this.resolveProductGroupNode(patientMonitor, groupNode);
        if (resolvedGroupNode) {
          this.initGroupIconElement(patientMonitor, icon, resolvedGroupNode, patientMonitor.itemTextVisible, select);
        } else {
          this.initIconElement(patientMonitor, icon, this.patientMonitorGroup, patientMonitor.itemTextVisible, select);
        }
      });
    }
  }

  addCentralStation(
    centralStation: FloorplanSimplePlaceholder | FloorplanCentralStation,
    select: boolean,
    groupNode?: Konva.Group,
  ): void {
    let icon;
    const resolvedGroupNode = this.resolveProductGroupNode(centralStation, groupNode);
    if (
      this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC ||
      centralStation instanceof FloorplanSimplePlaceholder
    ) {
      icon = this.imageFactory.createImage(
        centralStation instanceof FloorplanCentralStation
          ? ImageType.CENTRAL_STATION_ICON
          : ImageType.CENTRAL_STATION_PLACEHOLDER_ICON,
      );
      if (resolvedGroupNode) {
        this.initGroupIconElement(centralStation, icon, resolvedGroupNode, centralStation.itemTextVisible, select);
      } else {
        this.initIconElement(centralStation, icon, this.centralStationGroup, centralStation.itemTextVisible, select);
      }
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      this.imageFactory.createProductImage$(centralStation).subscribe((icon) => {
        const resolvedGroupNode = this.resolveProductGroupNode(centralStation, groupNode);
        if (resolvedGroupNode) {
          this.initGroupIconElement(centralStation, icon, resolvedGroupNode, centralStation.itemTextVisible, select);
        } else {
          this.initIconElement(centralStation, icon, this.centralStationGroup, centralStation.itemTextVisible, select);
        }
      });
    }
  }

  addTelemetry(telemetry: FloorplanSimplePlaceholder | FloorplanTelemetry, select: boolean, groupNode?: Konva.Group): void {
    let icon;
    const resolvedGroupNode = this.resolveProductGroupNode(telemetry, groupNode);
    if (
      this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC ||
      telemetry instanceof FloorplanSimplePlaceholder
    ) {
      icon = this.imageFactory.createImage(
        telemetry instanceof FloorplanTelemetry ? ImageType.TELEMETRY_ICON : ImageType.TELEMETRY_PLACEHOLDER_ICON,
      );
      if (resolvedGroupNode) {
        this.initGroupIconElement(telemetry, icon, resolvedGroupNode, telemetry.itemTextVisible, select);
      } else {
        this.initIconElement(telemetry, icon, this.telemetryGroup, telemetry.itemTextVisible, select);
      }
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      this.imageFactory.createProductImage$(telemetry).subscribe((icon) => {
        const resolvedGroupNode = this.resolveProductGroupNode(telemetry, groupNode);
        if (resolvedGroupNode) {
          this.initGroupIconElement(telemetry, icon, resolvedGroupNode, telemetry.itemTextVisible, select);
        } else {
          this.initIconElement(telemetry, icon, this.telemetryGroup, telemetry.itemTextVisible, select);
        }
      });
    }
  }

  addKvm(kvm: FloorplanSimplePlaceholder | FloorplanKvm, select: boolean, groupNode?: Konva.Group): void {
    if (kvm instanceof FloorplanSimplePlaceholder) {
      const icon = this.imageFactory.createImage(ImageType.KVM_PLACEHOLDER_ICON);
      if (groupNode) {
        this.initGroupIconElement(kvm, icon, groupNode, kvm.itemTextVisible, select);
      } else {
        this.initIconElement(kvm, icon, this.kvmGroup, kvm.itemTextVisible, select);
      }
      return;
    }

    const warningSignIcon = this.imageFactory.createImage(ImageType.WARNING_SIGN_ICON);
    if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC) {
      const icon = this.imageFactory.createImage(ImageType.KVM_ICON);
      this.handleAddKvm(kvm, icon, warningSignIcon, select, groupNode);
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      this.imageFactory.createProductImage$(kvm).subscribe((icon) => {
        this.handleAddKvm(kvm, icon, warningSignIcon, select, groupNode);
      });
    }
  }

  private handleAddKvm(
    kvm: FloorplanKvm,
    transmitterIcon: Konva.Image,
    warningSignIcon: Konva.Image,
    select: boolean,
    groupNode?: Konva.Group,
  ): void {
    const kvmShapes = KvmKonva.init(kvm, transmitterIcon, warningSignIcon, groupNode);
    this.initKonvaElement(kvmShapes.transmitter, select);
    this.setProductGroupItemTextVisibility(kvmShapes.kvmGroup, kvm.itemTextVisible);

    if (groupNode) {
      this.productGroups.add(groupNode);
      this.addItem(kvm, kvmShapes.kvmGroup, groupNode);
      groupNode.getLayer()?.batchDraw();
    } else {
      this.addItem(kvm, kvmShapes.kvmGroup, this.kvmGroup);
    }

    this.attachKvmReceivers(kvm);
  }

  attachKvmReceivers(kvm: FloorplanKvm): void {
    kvm.receivers.forEach((receiver) => this.attachKvmReceiver(kvm, receiver));
  }

  private attachKvmReceiver(kvm: FloorplanKvm, receiver: FloorplanKvmReceiver): void {
    const receiverIcon = this.imageFactory.createImage(ImageType.KVM_RECEIVER_ICON);
    const kvmGroup = this.getKonvaNode(kvm) as Konva.Group;
    const receiverShapes = KvmKonva.attachReceiver(receiver, receiverIcon, kvmGroup);
    this.initKonvaElement(receiverShapes.receiver, false);
    this.nodesByKvmReceiver.set(receiver, receiverShapes.group);
  }

  syncKvmReceivers(kvm: FloorplanKvm): void {
    const kvmGroup = this.getKonvaNode(kvm) as Konva.Group | undefined;

    if (!kvmGroup) {
      return;
    }

    const expectedReceiverIds = new Set(kvm.receivers.map((receiver) => receiver.id));
    const existingReceiverGroups = this.getKvmReceiverGroups(kvmGroup);

    existingReceiverGroups.forEach((receiverGroup) => {
      const receiver = receiverGroup.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanKvmReceiver;

      if (expectedReceiverIds.has(receiver.id)) {
        return;
      }

      if (this.isNodeWithin(receiverGroup, this.selectedNode)) {
        this.deselect();
      }

      KvmKonva.detachReceiver(receiverGroup, kvmGroup);
      this.nodesByKvmReceiver.delete(receiver);
    });

    const existingReceiverIds = new Set(this.getKvmReceiverGroups(kvmGroup).map((receiverGroup) => receiverGroup.id()));

    kvm.receivers.forEach((receiver) => {
      if (!existingReceiverIds.has(receiver.id)) {
        this.attachKvmReceiver(kvm, receiver);
      }
    });

    kvmGroup.getLayer()?.batchDraw();
  }

  addDisplay(display: FloorplanSimplePlaceholder | FloorplanDisplay, select: boolean, groupNode?: Konva.Group): void {
    if (
      this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC ||
      display instanceof FloorplanSimplePlaceholder
    ) {
      const icon = this.imageFactory.createImage(
        display instanceof FloorplanDisplay ? ImageType.DISPLAY_ICON : ImageType.DISPLAY_PLACEHOLDER_ICON,
      );
      if (groupNode) {
        this.initGroupIconElement(display, icon, groupNode, display.itemTextVisible, select);
      } else {
        this.initIconElement(display, icon, this.displayGroup, display.itemTextVisible, select);
      }
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      this.imageFactory.createProductImage$(display).subscribe((icon) => {
        if (groupNode) {
          this.initGroupIconElement(display, icon, groupNode, display.itemTextVisible, select);
        } else {
          this.initIconElement(display, icon, this.displayGroup, display.itemTextVisible, select);
        }
      });
    }
  }

  addUtility(utility: FloorplanSimplePlaceholder | FloorplanUtility, select: boolean, groupNode?: Konva.Group): void {
    let icon;
    if (
      this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC ||
      utility instanceof FloorplanSimplePlaceholder
    ) {
      icon = this.imageFactory.createImage(
        utility instanceof FloorplanUtility ? ImageType.UTILITY_ICON : ImageType.UTILITY_PLACEHOLDER_ICON,
      );
      if (groupNode) {
        this.initGroupIconElement(utility, icon, groupNode, utility.itemTextVisible, select);
      } else {
        this.initIconElement(utility, icon, this.utilityGroup, utility.itemTextVisible, select);
      }
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      this.imageFactory.createProductImage$(utility).subscribe((icon) => {
        if (groupNode) {
          this.initGroupIconElement(utility, icon, groupNode, utility.itemTextVisible, select);
        } else {
          this.initIconElement(utility, icon, this.utilityGroup, utility.itemTextVisible, select);
        }
      });
    }
  }

  addIsolationRoom(isolationRoom: IsolationRoom, select: boolean): void {
    const node = IsolationRoomKonva.init(isolationRoom);
    const isolationRoomGroup = node.getChildren()[0] as Konva.Group;
    const itemText = node.getChildren()[1] as Konva.Group;
    const text = FloorplanItemTextKonva.getText(itemText);

    this.initKonvaElement(isolationRoomGroup, select);
    ZoneKonva.setTextPosition(itemText, isolationRoomGroup);
    ZoneKonva.scaleText(text, isolationRoomGroup);
    this.setProductGroupItemTextVisibility(node, isolationRoom.itemTextVisible);
    isolationRoomGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(isolationRoomGroup);
    });
    this.addItem(isolationRoom, node, this.isolationRoomGroup);
  }

  addItRoom(itRoom: ItRoom, select: boolean): void {
    const node = ItRoomKonva.init(itRoom);
    const itRoomGroup = node.getChildren()[0] as Konva.Group;
    const itemText = node.getChildren()[1] as Konva.Group;
    const text = FloorplanItemTextKonva.getText(itemText);

    this.initKonvaElement(itRoomGroup, select);
    ZoneKonva.setTextPosition(itemText, itRoomGroup);
    ZoneKonva.scaleText(text, itRoomGroup);
    this.setProductGroupItemTextVisibility(node, itRoom.itemTextVisible);
    itRoomGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(itRoomGroup);
    });
    this.addItem(itRoom, node, this.itRoomGroup);
  }

  addOperatingTheatre(operatingTheatre: OperatingTheatre, select: boolean): void {
    const node = OperatingTheatreKonva.init(operatingTheatre);
    const operatingTheatreGroup = node.getChildren()[0] as Konva.Group;
    const itemText = node.getChildren()[1] as Konva.Group;
    const text = FloorplanItemTextKonva.getText(itemText);

    this.initKonvaElement(operatingTheatreGroup, select);
    ZoneKonva.setTextPosition(itemText, operatingTheatreGroup);
    ZoneKonva.scaleText(text, operatingTheatreGroup);
    this.setProductGroupItemTextVisibility(node, operatingTheatre.itemTextVisible);
    operatingTheatreGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(operatingTheatreGroup);
    });
    this.addItem(operatingTheatre, node, this.operatingTheatreGroup);
  }

  addProcedureRoom(procedureRoom: ProcedureRoom, select: boolean): void {
    const node = ProcedureRoomKonva.init(procedureRoom);
    const procedureRoomGroup = node.getChildren()[0] as Konva.Group;
    const itemText = node.getChildren()[1] as Konva.Group;
    const text = FloorplanItemTextKonva.getText(itemText);

    this.initKonvaElement(procedureRoomGroup, select);
    ZoneKonva.setTextPosition(itemText, procedureRoomGroup);
    ZoneKonva.scaleText(text, procedureRoomGroup);
    this.setProductGroupItemTextVisibility(node, procedureRoom.itemTextVisible);
    procedureRoomGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(procedureRoomGroup);
    });
    this.addItem(procedureRoom, node, this.procedureRoomGroup);
  }

  addStandardRoom(standardRoom: StandardRoom, select: boolean): void {
    const node = StandardRoomKonva.init(standardRoom);
    const standardRoomGroup = node.getChildren()[0] as Konva.Group;
    const itemText = node.getChildren()[1] as Konva.Group;
    const text = FloorplanItemTextKonva.getText(itemText);

    this.initKonvaElement(standardRoomGroup, select);
    ZoneKonva.setTextPosition(itemText, standardRoomGroup);
    ZoneKonva.scaleText(text, standardRoomGroup);
    this.setProductGroupItemTextVisibility(node, standardRoom.itemTextVisible);
    standardRoomGroup.on("transformend", () => {
      this.transformer.resetIfSingleSelect(standardRoomGroup);
    });
    this.addItem(standardRoom, node, this.standardRoomGroup);
  }

  createGroup(group: ProductItemGroup) {
    const groupNode = GroupKonva.init(group);
    this.productGroups.add(groupNode);

    const refsForThisFloorplan = group.references.filter((ref) => ref.floorplanId === this.floorplan.id);

    for (const ref of refsForThisFloorplan) {
      const item = this.resolveItemById(ref.floorplanProductItemId);
      if (!item) continue;

      const handler = this.groupHandlers.find((h) => h.match(item));
      if (!handler) continue;

      this.removeProductFromGroup(item, handler.ungroupedProducts);
      handler.addFn(item as never, groupNode);
    }
  }

  private resolveItemById(id: string): ProductTypesInGroup | undefined {
    return (
      this.floorplan.alarmDevices.find((i) => i.id === id) ??
      this.floorplan.alarmDevicePlaceholders.find((i) => i.id === id) ??
      this.floorplan.gasWarningCenters.find((i) => i.id === id) ??
      this.floorplan.gasWarningCenterPlaceholders.find((i) => i.id === id) ??
      this.floorplan.plasticSigns.find((i) => i.id === id) ??
      this.floorplan.plasticSignPlaceholders.find((i) => i.id === id) ??
      this.floorplan.signalElements.find((i) => i.id === id) ??
      this.floorplan.signalElementPlaceholders.find((i) => i.id === id) ??
      this.floorplan.transmitters.find((i) => i.id === id) ??
      this.floorplan.transmitterPlaceholders.find((i) => i.id === id) ??
      this.floorplan.patientMonitors.find((i) => i.id === id) ??
      this.floorplan.patientMonitorPlaceholders.find((i) => i.id === id) ??
      this.floorplan.centralStations.find((i) => i.id === id) ??
      this.floorplan.centralStationPlaceholders.find((i) => i.id === id) ??
      this.floorplan.telemetries.find((i) => i.id === id) ??
      this.floorplan.telemetryPlaceholders.find((i) => i.id === id) ??
      this.floorplan.kvms.find((i) => i.id === id) ??
      this.floorplan.kvmPlaceholders.find((i) => i.id === id) ??
      this.floorplan.displays.find((i) => i.id === id) ??
      this.floorplan.displayPlaceholders.find((i) => i.id === id) ??
      this.floorplan.utilities.find((i) => i.id === id) ??
      this.floorplan.utilityPlaceholders.find((i) => i.id === id)
    );
  }

  deleteGroup(group: ProductItemGroup) {
    const konvaGroup = this.findKonvaGroup(group);
    const children = [...konvaGroup.getChildren()];
    children.forEach((child: Konva.Node) => {
      const product = child.getAttr("floorplanObject");

      this.removeGroupItem(product, konvaGroup);
    });
    konvaGroup.destroy();
  }

  removeFromGroup(group: { product: ProductTypesInGroup; sourceGroup: ProductItemGroup }) {
    const sourceGroup = this.findKonvaGroup(group.sourceGroup);
    this.removeGroupItem(group.product, sourceGroup);
  }

  removeProductFromGroup(floorplanItem: ProductTypesInGroup, group: Konva.Group) {
    const node = this.getKonvaNode(floorplanItem);
    if (node) {
      if (this.selectedNode?.getAttr("floorplanObject") === floorplanItem) {
        this.deselect();
      }
      node.remove();
      group.getLayer()?.draw();
    }
  }

  changeGroup(group: { product: ProductTypesInGroup; targetGroup: ProductItemGroup; sourceGroup: ProductItemGroup }) {
    const product = group.product;
    let targetGroup = this.findKonvaGroup(group.targetGroup);

    // If the target group has no Konva node yet on this floorplan (group was created on another floorplan),
    // create the Konva group node now so the group chip is shown correctly.
    if (!targetGroup) {
      const groupNode = GroupKonva.init(group.targetGroup);
      this.productGroups.add(groupNode);
      targetGroup = groupNode;
    }

    const existingNode = this.getKonvaNode(product);

    if (!existingNode) {
      return;
    }

    if (existingNode.getParent() === targetGroup) {
      return;
    }

    if (group.sourceGroup) {
      const currentGroup = this.findKonvaGroup(group.sourceGroup);
      if (currentGroup) {
        this.removeProductFromGroup(product, currentGroup);
      }
      this.moveProductToGroup(product, targetGroup);
    } else {
      for (const handler of this.groupHandlers) {
        if (handler.match(product)) {
          this.removeProductFromGroup(product, handler.ungroupedProducts);
          handler.addFn(product as any, targetGroup);
          break;
        }
      }
    }
  }

  moveProductToGroup(product: ProductTypesInGroup, group: Konva.Group) {
    for (const handler of this.groupHandlers) {
      if (handler.match(product)) {
        handler.addFn(product as any, group);
        break;
      }
    }
  }

  removeGroupItem(product: ProductTypesInGroup, group: Konva.Group) {
    for (const handler of this.groupHandlers) {
      if (handler.match(product)) {
        this.removeProductFromGroup(product, group);
        handler.addFn(product as any);
        break;
      }
    }
  }

  selectGroup(group: ProductItemGroup) {
    const konvaGroup = this.findKonvaGroup(group);

    if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC) {
      this.productGroups.getChildren().forEach((group) => {
        if (group.attrs.groupId === konvaGroup.attrs.groupId) {
          this.setGroupIcon(konvaGroup, true);
        } else {
          this.setGroupIcon(group as Konva.Group);
        }
      });
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      this.productGroups.getChildren().forEach((group) => {
        if (group.attrs.groupId === konvaGroup.attrs.groupId) {
          this.setGroupProductImage(konvaGroup, true);
        } else {
          this.setGroupProductImage(group as Konva.Group);
        }
      });
    }
  }

  deselectGroup(group: ProductItemGroup) {
    const konvaGroup = this.findKonvaGroup(group);

    if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.SCHEMATIC) {
      this.setGroupIcon(konvaGroup);
    } else if (this.floorplan.floorplanState.displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      this.setGroupProductImage(konvaGroup);
    }
  }

  findKonvaGroup(group: ProductItemGroup) {
    const children = this.productGroups.getChildren();
    return children.find((child: Konva.Node) => {
      const childId = child.getAttr("id");
      return childId === group.id;
    }) as Konva.Group;
  }

  attachRemoteSensor(transmitter: FloorplanTransmitter | FloorplanTransmitterPlaceholder, select: boolean) {
    const remoteSensorIcon = this.imageFactory.createImage(ImageType.REMOTE_SENSOR);
    const transmitterGroup = this.getKonvaNode(transmitter);
    const remoteSensorKonvaShapes = TransmitterKonva.attachRemoteSensor(
      transmitter,
      remoteSensorIcon,
      transmitterGroup as Konva.Group,
    );
    this.initKonvaElement(remoteSensorKonvaShapes.remoteSensor, select);
    this.initKonvaElement(remoteSensorKonvaShapes.monitoringArea);
    if (transmitter.remoteSensor) {
      this.nodesByRemoteSensor.set(transmitter.remoteSensor, remoteSensorKonvaShapes.group);
    }
  }

  detachRemoteSensor(transmitter: FloorplanTransmitter | FloorplanTransmitterPlaceholder) {
    const transmitterGroup = this.getKonvaNode(transmitter);
    if (transmitter.remoteSensor) {
      this.nodesByRemoteSensor.delete(transmitter.remoteSensor);
    }
    TransmitterKonva.detachRemoteSensor(transmitterGroup as Konva.Group);
  }

  deleteItem(floorplanItem: FloorplanItem) {
    const node = this.getKonvaNode(floorplanItem);
    if (node) {
      // Destroy the coverage area cone if it exists (stored separately in flameDetectorConeGroup)
      const cone = FlameDetectorKonva.getCoverageAreaCone(node as Konva.Group);
      if (cone) {
        cone.destroy();
      }

      node.destroy();
      if (this.selectedNode?.getAttr("floorplanObject") === floorplanItem || !this.selectedNode?.getLayer()) {
        this.deselect();
      }
      this.nodesByFloorplanItem.delete(floorplanItem);

      // Clean up multi-select visuals for any nodes that were children of the destroyed node
      this.selectedNodes.forEach((selectedNode) => {
        if (!selectedNode.getLayer()) {
          this.removeMultiSelectVisuals(selectedNode);
          this.selectedNodes.delete(selectedNode);
        }
      });

      // Also clean up any receiver/sensor nodes from the destroyed group
      this.nodesByKvmReceiver.forEach((receiverNode, receiver) => {
        if (!receiverNode.getLayer()) {
          this.nodesByKvmReceiver.delete(receiver);
        }
      });
      this.nodesByRemoteSensor.forEach((sensorNode, sensor) => {
        if (!sensorNode.getLayer()) {
          this.nodesByRemoteSensor.delete(sensor);
        }
      });

      if (this.selectedNodes.size === 0) {
        this.transformer.clear();
      }
    }
    if (this.pendingSelection?.item === floorplanItem) {
      this.pendingSelection = undefined;
    }
  }

  refreshReferenceMeasure(referenceMeasure: ReferenceMeasure): void {
    const group = this.nodesByFloorplanItem.get(referenceMeasure) as Konva.Group;
    ReferenceMeasureKonva.refreshReferenceMeasureText(referenceMeasure, group);
  }

  resetReferenceMeasure(referenceMeasure: { floorplanItem: ReferenceMeasure; currentData: ReferenceMeasureData }) {
    const group = this.nodesByFloorplanItem.get(referenceMeasure.floorplanItem) as Konva.Group;
    ReferenceMeasureKonva.resetReferenceMeasure(group, referenceMeasure.currentData, referenceMeasure.floorplanItem);
    this.transformer.reset(group.getChildren()[0] as Konva.Group);
  }

  refreshFreeText(freeText: FloorplanFreeText) {
    const node = this.nodesByFloorplanItem.get(freeText);
    if (!node || !(node instanceof Konva.Group)) {
      return;
    }
    FreeTextKonva.refreshText(freeText, node);
  }

  refreshDisplayName(floorplanItem: FloorplanItem) {
    this.refreshItemText(floorplanItem);
  }

  refreshItemText(floorplanItem: FloorplanItem): void {
    const group = this.nodesByFloorplanItem.get(floorplanItem) as Konva.Group;
    if (!group) {
      return;
    }

    if (floorplanItem instanceof ExZone) {
      ExZoneKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof DustExZone) {
      DustExZoneKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof WildcardZone) {
      WildcardZoneKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof DangerArea) {
      DangerAreaKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof MeasurementLine) {
      MeasurementLineKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof ReferenceMeasure) {
      ReferenceMeasureKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof AirPath) {
      const airPathGroup = group.getChildren()[0] as Konva.Group | undefined;
      const textGroup = group.findOne(".itemText") as Konva.Group | null;
      if (airPathGroup && textGroup) {
        AirPathKonva.refreshItemText(floorplanItem, airPathGroup, textGroup);
      }
    } else if (floorplanItem instanceof Pipeline) {
      PipelineKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof CableRoute) {
      CableRouteKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof ProtectiveTube) {
      ProtectiveTubeKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof Wire) {
      WireKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof FloorplanImage) {
      ImageKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof FloorplanFreeText) {
      FreeTextKonva.refreshText(floorplanItem, group);
    } else if (floorplanItem instanceof FloorplanTransmitter || floorplanItem instanceof FloorplanTransmitterPlaceholder) {
      TransmitterKonva.refreshTransmitterItemText(floorplanItem, group);
    } else if (
      floorplanItem instanceof FloorplanProductItem ||
      floorplanItem instanceof FloorplanSimplePlaceholder ||
      floorplanItem instanceof FloorplanGasWarningCenterPlaceholder ||
      floorplanItem instanceof FloorplanFlameDetectorPlaceholder ||
      floorplanItem instanceof FloorplanText
    ) {
      IconKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof IsolationRoom) {
      IsolationRoomKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof ItRoom) {
      ItRoomKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof OperatingTheatre) {
      OperatingTheatreKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof ProcedureRoom) {
      ProcedureRoomKonva.refreshItemText(floorplanItem, group);
    } else if (floorplanItem instanceof StandardRoom) {
      StandardRoomKonva.refreshItemText(floorplanItem, group);
    }
  }

  deselect(): void {
    this.clearMultiSelectVisuals();
    if (this.selectedNode) this.updateNodeDraggable(this.selectedNode, false);
    this.selectedNodes.forEach((n) => this.updateNodeDraggable(n, false));
    this.selectedNode = undefined;
    this.selectedNodes.clear();
    this.pendingSelection = undefined;
    this._selectedItemChanged$.next({ item: null, selectIntention: null });
    this._selectedItemsChanged$.next([]);
    this.transformer.clear();
  }

  getSelectedItems(): FloorplanItem[] {
    const selectedItems: FloorplanItem[] = [];
    this.selectedNodes.forEach((node) => {
      const item = node.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY);
      if (item instanceof FloorplanItem) {
        selectedItems.push(item);
      }
    });
    return selectedItems;
  }

  getSelectedItem(): FloorplanItem | null {
    if (this.selectedNode) {
      const item = this.selectedNode.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY);
      if (item instanceof FloorplanItem) {
        return item;
      }
    }
    return null;
  }

  private isIconTypeNode(node: Konva.Node): boolean {
    const item = node.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY);
    return (
      item instanceof FloorplanProductItem ||
      item instanceof FloorplanSimplePlaceholder ||
      item instanceof FloorplanTransmitterPlaceholder ||
      item instanceof FloorplanGasWarningCenterPlaceholder ||
      item instanceof FloorplanFlameDetectorPlaceholder ||
      item instanceof FloorplanText ||
      item instanceof FloorplanKvmReceiver ||
      item instanceof FloorplanRemoteSensor
    );
  }

  private applyIconMultiSelectColor(node: Konva.Node): void {
    if (!(node instanceof Konva.Image)) return;
    const currentImg = node.image() as HTMLImageElement | undefined;
    if (!currentImg?.src?.startsWith("data:image/svg+xml")) return;

    node.setAttr("_msOrigImg", currentImg);
    const blueDataUrl = recolorSvgDataUrl(currentImg.src, IconColor.MULTI_SELECT_COLOR);
    const newImg = new window.Image();
    newImg.onload = () => {
      // Guard: only apply if the node hasn't been deselected in the meantime
      if (node.getAttr("_msOrigImg") === currentImg) {
        node.image(newImg);
      }
    };
    newImg.src = blueDataUrl;
  }

  private resetIconMultiSelectColor(node: Konva.Node): void {
    if (!(node instanceof Konva.Image)) return;
    const orig = node.getAttr("_msOrigImg") as HTMLImageElement | undefined;
    if (orig) {
      node.image(orig);
      node.setAttr("_msOrigImg", undefined);
    }
  }

  private addMultiSelectVisuals(node: Konva.Node): void {
    const getRect = () => node.getClientRect({ relativeTo: this.itemLayer });
    const r = getRect();

    const border = new Konva.Rect({
      x: r.x,
      y: r.y,
      width: r.width,
      height: r.height,
      fill: "transparent",
      stroke: "#4EADFF",
      strokeWidth: 1,
      strokeScaleEnabled: false,
      listening: false,
    });
    this.itemLayer.add(border);
    border.moveToTop();
    this.multiSelectBorders.set(node, border);

    if (this.isIconTypeNode(node)) {
      this.applyIconMultiSelectColor(node);
    }

    const syncBorder = () => {
      const updated = getRect();
      border.setAttrs({ x: updated.x, y: updated.y, width: updated.width, height: updated.height });
    };
    node.on("dragmove.multiselect transform.multiselect transformend.multiselect", syncBorder);
  }

  private removeMultiSelectVisuals(node: Konva.Node): void {
    node.off("dragmove.multiselect transform.multiselect transformend.multiselect");
    if (this.isIconTypeNode(node)) {
      this.resetIconMultiSelectColor(node);
    }
    const border = this.multiSelectBorders.get(node);
    if (border) {
      border.destroy();
      this.multiSelectBorders.delete(node);
    }
  }

  private clearMultiSelectVisuals(): void {
    this.selectedNodes.forEach((node) => {
      node.off("dragmove.multiselect transform.multiselect transformend.multiselect");
      if (this.isIconTypeNode(node)) {
        this.resetIconMultiSelectColor(node);
      }
    });
    this.multiSelectBorders.forEach((border) => border.destroy());
    this.multiSelectBorders.clear();
  }

  selectItem(floorplanItem: FloorplanItem, selectIntention: ItemSelectIntention = ItemSelectIntention.REGULAR): void {
    const node = this.getKonvaNode(floorplanItem);
    if (!node) {
      this.pendingSelection = { item: floorplanItem, selectIntention };
      return;
    }

    this.pendingSelection = undefined;
    this.selectNode(this.resolveSelectableNode(node, floorplanItem), selectIntention);
  }

  getPointerPosition(): Point {
    return this.stage.getRelativePointerPosition()!;
  }

  exportFloorplanToPngUrl(): string {
    const { scaleX, scaleY, stageHeight, stageWidth, stageX, stageY, exportResult } = this.setStageToInitialSizeForExport(
      (exportWidth, exportHeight) => this.exportFloorplanWithFallback(exportWidth, exportHeight),
    );
    this.resetStageAfterExport(scaleX, scaleY, stageHeight, stageWidth, stageX, stageY);
    return exportResult;
  }

  exportFloorplanLayersForPdf(): FloorplanExportLayers {
    const { scaleX, scaleY, stageHeight, stageWidth, stageX, stageY, exportResult } = this.setStageToInitialSizeForExport(
      (exportWidth, exportHeight) => this.exportFloorplanLayers(exportWidth, exportHeight),
    );
    this.resetStageAfterExport(scaleX, scaleY, stageHeight, stageWidth, stageX, stageY);
    return exportResult;
  }

  waitForProductImages$(): Observable<undefined> {
    if (!this.pendingProductImageLoads.length) {
      return of(undefined);
    }
    const pending = this.pendingProductImageLoads;
    this.pendingProductImageLoads = [];
    return forkJoin(pending).pipe(map(() => undefined));
  }

  waitForFloorplanImages$(): Observable<undefined> {
    if (!this.pendingFloorplanImageLoads.length) {
      return of(undefined);
    }
    const pending = this.pendingFloorplanImageLoads;
    this.pendingFloorplanImageLoads = [];
    return forkJoin(pending).pipe(map(() => undefined));
  }

  getStageDimensions(): { width: number; height: number } {
    return { width: this.stage.width(), height: this.stage.height() };
  }

  getCenterPoint(): Point {
    const centerX = this.stage.container().offsetWidth / this.stage.scaleX() / 2 - this.stage.x() / this.stage.scaleX();
    const centerY = this.stage.container().offsetHeight / this.stage.scaleY() / 2 - this.stage.y() / this.stage.scaleY();

    return new Point(centerX, centerY);
  }

  setScale(factor: number) {
    this.stage.scale({ x: factor, y: factor });
  }

  setStagePosition(point: Point) {
    this.stage.position(point);
  }

  setDisplayMode(displayMode: string) {
    if (displayMode === FloorplanDisplayMode.SCHEMATIC) {
      this.setIcon(this.gasWarningCenterGroup, ImageType.GAS_WARNING_CENTER_ICON);
      this.setIcon(this.alarmDeviceGroup, ImageType.ALARM_DEVICE_ICON);
      this.setIcon(this.signalElementGroup, ImageType.SIGNAL_ELEMENT_ICON);
      this.setIcon(this.plasticSignGroup, ImageType.PLASTIC_SIGN_ICON);
      this.setIcon(this.patientMonitorGroup, ImageType.PATIENT_MONITOR_ICON);
      this.setIcon(this.centralStationGroup, ImageType.CENTRAL_STATION_ICON);
      this.setIcon(this.telemetryGroup, ImageType.TELEMETRY_ICON);
      this.setIcon(this.kvmGroup, ImageType.KVM_ICON);
      this.setIcon(this.displayGroup, ImageType.DISPLAY_ICON);
      this.setIcon(this.utilityGroup, ImageType.UTILITY_ICON);
      this.setTransmitterIcon(this.transmitterGroup, ImageType.TRANSMITTER_ICON);

      let konvaGroup: Konva.Group;
      const selectedGroupId = this.floorplan.floorplanState.selectedGroupId;
      if (selectedGroupId) {
        const selectedGroup = this.floorplan.project.groups.find((g) => g.id === selectedGroupId);
        konvaGroup = this.findKonvaGroup(selectedGroup!);
      }
      this.productGroups.getChildren().forEach((group) => {
        if (konvaGroup && group === konvaGroup) {
          this.setGroupIconForCurrentFloorplan(group as Konva.Group, selectedGroupId!);
        } else {
          this.setGroupIcon(group as Konva.Group);
        }
      });
    } else if (displayMode === FloorplanDisplayMode.PRODUCT_IMAGE) {
      this.setProductImage(this.gasWarningCenterGroup);
      this.setProductImage(this.alarmDeviceGroup);
      this.setProductImage(this.signalElementGroup);
      this.setProductImage(this.plasticSignGroup);
      this.setProductImage(this.transmitterGroup);
      this.setProductImage(this.patientMonitorGroup);
      this.setProductImage(this.centralStationGroup);
      this.setProductImage(this.telemetryGroup);
      this.setProductImage(this.utilityGroup);
      this.setProductImage(this.displayGroup);
      this.setProductImage(this.kvmGroup);
      this.productGroups.getChildren().forEach((group) => {
        if (this.floorplan.floorplanState.selectedGroupId === group.attrs.id) {
          this.setProductImageForCurrentFloorplan(group as Konva.Group, this.floorplan.floorplanState.selectedGroupId!);
        } else {
          this.setProductImage(group as Konva.Group);
        }
      });
    }
  }

  setGroupProductImage(group: Konva.Group, isInGroup?: boolean) {
    this.forEachGroupVisual(group, ({ itemGroup, floorplanItem, imageNode }) => {
      if (!this.isProductImageCapable(floorplanItem)) {
        this.applyGroupedIcon(imageNode, floorplanItem, isInGroup);
        return;
      }

      this.imageFactory.setProductImage$(itemGroup, imageNode, floorplanItem, isInGroup).subscribe(() => {
        this.refreshItemText(floorplanItem);
      });
    });
  }

  setGroupIcon(group: Konva.Group, isInGroup?: boolean) {
    this.forEachGroupVisual(group, ({ floorplanItem, imageNode }) => {
      this.applyGroupedIcon(imageNode, floorplanItem, isInGroup);
    });
  }

  private setGroupIconForCurrentFloorplan(group: Konva.Group, selectedGroupId: string) {
    const selectedGroup = this.floorplan.project.groups.find((g) => g.id === selectedGroupId);
    this.forEachGroupVisual(group, ({ floorplanItem, imageNode }) => {
      const isOnCurrentFloorplan =
        selectedGroup?.references.some(
          (r) => r.floorplanId === this.floorplan.id && r.floorplanProductItemId === floorplanItem.id,
        ) ?? false;
      this.applyGroupedIcon(imageNode, floorplanItem, isOnCurrentFloorplan || undefined);
    });
  }

  private setProductImageForCurrentFloorplan(group: Konva.Group, selectedGroupId: string) {
    const selectedGroup = this.floorplan.project.groups.find((g) => g.id === selectedGroupId);
    group.getChildren().forEach((item: Konva.Node) => {
      const floorplanItem = item.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
      const isOnCurrentFloorplan =
        selectedGroup?.references.some(
          (r) => r.floorplanId === this.floorplan.id && r.floorplanProductItemId === (floorplanItem as { id?: string }).id,
        ) ?? false;

      if (
        item instanceof Group &&
        (floorplanItem instanceof FloorplanGasWarningCenter ||
          floorplanItem instanceof FloorplanAlarmDevice ||
          floorplanItem instanceof FloorplanSignalElement ||
          floorplanItem instanceof FloorplanPlasticSign ||
          floorplanItem instanceof FloorplanPatientMonitor ||
          floorplanItem instanceof FloorplanCentralStation ||
          floorplanItem instanceof FloorplanTelemetry ||
          floorplanItem instanceof FloorplanKvm ||
          floorplanItem instanceof FloorplanDisplay ||
          floorplanItem instanceof FloorplanUtility)
      ) {
        const imageNode =
          floorplanItem instanceof FloorplanKvm
            ? KvmKonva.getTransmitterIcon(item as Konva.Group)
            : (item.getChildren()[1] as Konva.Image);
        this.imageFactory.setProductImage$(item, imageNode, floorplanItem, isOnCurrentFloorplan || undefined).subscribe(() => {
          this.refreshItemText(floorplanItem);
        });
      } else if (item instanceof Group && floorplanItem instanceof FloorplanTransmitter) {
        const imageNode = item.getChildren()[1] as Konva.Group;
        const imageNodeChild = imageNode.getChildren()[1] as Konva.Image;
        this.imageFactory
          .setProductImage$(item as Konva.Group, imageNodeChild!, floorplanItem, isOnCurrentFloorplan || undefined)
          .subscribe(() => {
            this.refreshItemText(floorplanItem);
          });
      }
    });
  }

  setProductImage(group: Konva.Group, isInGroup?: boolean) {
    group.getChildren().forEach((item: Konva.Node) => {
      const floorplanItem = item.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;

      if (
        item instanceof Group &&
        (floorplanItem instanceof FloorplanGasWarningCenter ||
          floorplanItem instanceof FloorplanAlarmDevice ||
          floorplanItem instanceof FloorplanSignalElement ||
          floorplanItem instanceof FloorplanPlasticSign ||
          floorplanItem instanceof FloorplanPatientMonitor ||
          floorplanItem instanceof FloorplanCentralStation ||
          floorplanItem instanceof FloorplanTelemetry ||
          floorplanItem instanceof FloorplanKvm ||
          floorplanItem instanceof FloorplanDisplay ||
          floorplanItem instanceof FloorplanUtility)
      ) {
        const imageNode =
          floorplanItem instanceof FloorplanKvm
            ? KvmKonva.getTransmitterIcon(item as Konva.Group)
            : (item.getChildren()[1] as Konva.Image);

        this.imageFactory.setProductImage$(item, imageNode, floorplanItem, isInGroup).subscribe(() => {
          this.refreshItemText(floorplanItem);
        });
      } else if (item instanceof Group && floorplanItem instanceof FloorplanTransmitter) {
        const imageNode = item.getChildren()[1] as Konva.Group;

        const imageNodeChild = imageNode.getChildren()[1] as Konva.Image;
        this.imageFactory.setProductImage$(item as Konva.Group, imageNodeChild!, floorplanItem, isInGroup).subscribe(() => {
          this.refreshItemText(floorplanItem);
        });
      }
    });
  }

  setTransmitterIcon(group: Konva.Group, imageType: ImageType) {
    group.getChildren().forEach((item: Konva.Node) => {
      const floorplanItem = item.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
      if (item instanceof Group && floorplanItem instanceof FloorplanTransmitter) {
        this.imageFactory
          .setIcon$(imageType, (item.getChildren()[1] as Konva.Group).getChildren()[1] as Konva.Image)
          .subscribe(() => {
            this.refreshItemText(floorplanItem);
          });
      }
    });
  }

  setIcon(group: Konva.Group, imageType: ImageType) {
    group.getChildren().forEach((item: Konva.Node) => {
      const floorplanItem = item.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;

      if (
        item instanceof Group &&
        (floorplanItem instanceof FloorplanGasWarningCenter ||
          floorplanItem instanceof FloorplanAlarmDevice ||
          floorplanItem instanceof FloorplanSignalElement ||
          floorplanItem instanceof FloorplanPlasticSign ||
          floorplanItem instanceof FloorplanPatientMonitor ||
          floorplanItem instanceof FloorplanCentralStation ||
          floorplanItem instanceof FloorplanTelemetry ||
          floorplanItem instanceof FloorplanDisplay ||
          floorplanItem instanceof FloorplanUtility)
      ) {
        this.imageFactory.setIcon$(imageType, item.getChildren()[1] as Konva.Image).subscribe(() => {
          this.refreshItemText(floorplanItem);
        });
      } else if (item instanceof Group && floorplanItem instanceof FloorplanKvm) {
        const kvmIcon = KvmKonva.getTransmitterIcon(item as Konva.Group);
        this.imageFactory.setIcon$(imageType, kvmIcon).subscribe(() => {
          this.refreshItemText(floorplanItem);
        });
      }
    });
  }

  refreshFloorplanOpacity(value: number) {
    const konvaOpacity: number = value > 0 ? +value / 100 : value === 0 ? 0 : 1;
    this.floorplanLayer.getChildren()[1].opacity(konvaOpacity);
  }

  refreshWarningSignVisibility(
    floorplanItem: FloorplanProductItem<ProductConfiguration> | FloorplanTransmitterPlaceholder | FloorplanSimplePlaceholder,
    visible: boolean,
  ) {
    const node = this.nodesByFloorplanItem.get(floorplanItem);

    if (!node || !(node instanceof Konva.Group)) {
      return;
    }

    if (floorplanItem instanceof FloorplanTransmitter || floorplanItem instanceof FloorplanTransmitterPlaceholder) {
      TransmitterKonva.refreshWarningSignVisibility(node, visible);
    } else if (floorplanItem instanceof FloorplanFlameDetector || floorplanItem instanceof FloorplanFlameDetectorPlaceholder) {
      FlameDetectorKonva.refreshWarningSignVisibility(node, visible);
    } else if (floorplanItem instanceof FloorplanKvm) {
      IconKonva.refreshWarningSignVisibility(KvmKonva.getIconGroup(node as Konva.Group), visible);
    } else {
      IconKonva.refreshWarningSignVisibility(node, visible);
    }
  }

  getKonvaNode(floorplanItem: FloorplanItem) {
    return this.nodesByFloorplanItem.get(floorplanItem);
  }

  refreshZone(
    zone:
      ExZone | DustExZone | DangerArea | WildcardZone | IsolationRoom | ItRoom | OperatingTheatre | ProcedureRoom | StandardRoom,
  ): void {
    const zoneGroup = this.getKonvaNode(zone) as Konva.Group;
    if (zone instanceof ExZone) {
      ExZoneKonva.refreshSize(zone, zoneGroup);
    }

    if (zone instanceof DustExZone) {
      DustExZoneKonva.refreshSize(zone, zoneGroup, zone.width, zone.height);
    }

    if (zone instanceof WildcardZone) {
      WildcardZoneKonva.refreshSize(zone, zoneGroup);
    }

    if (zone instanceof DangerArea) {
      DangerAreaKonva.refreshSize(zone, zoneGroup, zone.width, zone.height);
    }

    if (zone instanceof IsolationRoom) {
      IsolationRoomKonva.refreshSize(zone, zoneGroup);
    }

    if (zone instanceof ItRoom) {
      ItRoomKonva.refreshSize(zone, zoneGroup);
    }

    if (zone instanceof OperatingTheatre) {
      OperatingTheatreKonva.refreshSize(zone, zoneGroup);
    }

    if (zone instanceof ProcedureRoom) {
      ProcedureRoomKonva.refreshSize(zone, zoneGroup);
    }

    if (zone instanceof StandardRoom) {
      StandardRoomKonva.refreshSize(zone, zoneGroup);
    }
    this.transformer.reset(this.selectedNode!);
  }

  refreshAirPath(airPath: AirPath) {
    const airPathGroup = this.getKonvaNode(airPath) as Group;
    AirPathKonva.refreshLength(airPath, airPathGroup);
    this.transformer.reset(this.selectedNode!);
  }

  refreshCableRoute(cableRoute: CableRoute) {
    const cableRouteGroup = this.getKonvaNode(cableRoute) as Group;
    CableRouteKonva.refreshLength(cableRoute, cableRouteGroup);
    this.transformer.reset(this.selectedNode!);
  }

  refreshPipeline(pipeline: Pipeline) {
    const pipelineGroup = this.getKonvaNode(pipeline) as Group;
    PipelineKonva.refreshLength(pipeline, pipelineGroup);
    this.transformer.reset(this.selectedNode!);
  }

  refreshProtectiveTube(protectiveTube: ProtectiveTube) {
    const protectiveTubeGroup = this.getKonvaNode(protectiveTube) as Group;
    ProtectiveTubeKonva.refreshLength(protectiveTube, protectiveTubeGroup);
    this.transformer.reset(this.selectedNode!);
  }

  refreshWire(wire: Wire) {
    const wireGroup = this.getKonvaNode(wire) as Group;
    WireKonva.refreshLength(wire, wireGroup);
    this.transformer.reset(this.selectedNode!);
  }

  refreshMeasurementLine(measurementLine: MeasurementLine) {
    const measurementLineGroup = this.getKonvaNode(measurementLine) as Group;
    MeasurementLineKonva.refreshLength(measurementLine, measurementLineGroup);
    MeasurementLineKonva.refreshMeasurementText(measurementLine, measurementLineGroup);

    this.transformer.reset(this.selectedNode!);
  }

  autoScaleItems() {
    (this.transmitterGroup.getChildren() as Konva.Group[]).forEach((transmitter) => {
      const floorplanItem = transmitter.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
      if (floorplanItem instanceof FloorplanTransmitter || floorplanItem instanceof FloorplanTransmitterPlaceholder) {
        TransmitterKonva.setMonitoringAreaRadius(transmitter, floorplanItem.monitoringAreaRadius);
      }
    });

    (this.flameDetectorGroup.getChildren() as Konva.Group[]).forEach((detector) => {
      const floorplanItem = detector.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
      if (floorplanItem instanceof FloorplanFlameDetector) {
        FlameDetectorKonva.setCoverageAreaRadius(detector, floorplanItem.coverageAreaLength);
      } else if (floorplanItem instanceof FloorplanFlameDetectorPlaceholder) {
        FlameDetectorKonva.setCoverageAreaRadius(detector, floorplanItem.coverageArea);
      }
    });

    (this.productGroups.getChildren() as Konva.Group[]).forEach((group) => {
      (group.getChildren() as Konva.Group[]).forEach((product) => {
        const floorplanItem = product.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
        if (floorplanItem instanceof FloorplanTransmitter || floorplanItem instanceof FloorplanTransmitterPlaceholder) {
          TransmitterKonva.setMonitoringAreaRadius(product, floorplanItem.monitoringAreaRadius);
        }
        if (floorplanItem instanceof FloorplanFlameDetector) {
          FlameDetectorKonva.setCoverageAreaRadius(product, floorplanItem.coverageAreaLength);
        } else if (floorplanItem instanceof FloorplanFlameDetectorPlaceholder) {
          FlameDetectorKonva.setCoverageAreaRadius(product, (floorplanItem as FloorplanFlameDetectorPlaceholder).coverageArea);
        }
      });
    });
  }

  private setMonitoringAreaVisibility(visible: boolean): boolean {
    this.transmitterGroup
      .getChildren()
      .forEach((transmitter) => TransmitterKonva.setMonitoringAreaVisibility(transmitter as Konva.Group, visible));
    this.productGroups.getChildren().forEach((group) => {
      this.setGroupMonitoringAreaVisibility(group as Konva.Group, visible);
    });
    return visible;
  }

  private setCoverageAreaConeVisibility(visible: boolean): void {
    this.flameDetectorConeGroup.getChildren().forEach((cone) => cone.visible(visible));
    this.flameDetectorGroup
      .getChildren()
      .forEach((detector) => FlameDetectorKonva.setCoverageAreaVisibility(detector as Konva.Group, visible));
    this.productGroups.getChildren().forEach((group) => {
      this.setGroupCoverageAreaConeVisibility(group as Konva.Group, visible);
    });
  }

  private initFloorplan(floorplan: Floorplan, floorplanShape: Konva.Shape, container: HTMLDivElement) {
    this.floorplan = floorplan;
    this.floorplanBorderShape = FloorplanKonva.initBorder(floorplanShape);
    floorplan.floorplanState.updateBorderShape(this.getBorderDimensions());
    FloorplanKonva.setFloorplanInCenterOfBorder(floorplanShape, this.floorplanBorderShape);
    this.floorplanShape = floorplanShape;

    this.initStage(floorplan.floorplanState, container);
    this.initResizeHandler();
    this.initStageTapHandler();
    this.initDragOverHandler();
    this.initDropHandler();
    this.initStageDragHandler(floorplan.floorplanState);
    this.initStageWheelHandler(floorplan.floorplanState);
    this.initStageTouchHandler(floorplan.floorplanState);
    this.initSelectionRectHandler(floorplan.floorplanState);

    this.initFloorplanLayer(floorplanShape, this.floorplanBorderShape);
    this.initItemLayer(floorplan.floorplanState);
    this.refreshFloorplanOpacity(floorplan.floorplanState.opacity);
  }

  private initStage(floorplanState: FloorplanState, container: HTMLDivElement) {
    this.stage = new Konva.Stage({
      container: container,
      scaleX: floorplanState.scale,
      scaleY: floorplanState.scale,
      x: floorplanState.x,
      y: floorplanState.y,
      width: container.clientWidth,
      height: container.clientHeight,
      draggable: true,
    });
  }

  private initFloorplanLayer(floorplanShape: Konva.Shape, floorplanBorderShape: Konva.Rect) {
    this.floorplanLayer.add(floorplanBorderShape);
    this.floorplanLayer.add(floorplanShape);
    this.stage.add(this.floorplanLayer);
  }

  private resetStageAfterExport(
    scaleX: number,
    scaleY: number,
    stageHeight: number,
    stageWidth: number,
    stageX: number,
    stageY: number,
  ) {
    this.stage.scaleX(scaleX);
    this.stage.scaleY(scaleY);
    this.stage.height(stageHeight);
    this.stage.width(stageWidth);
    this.stage.x(stageX);
    this.stage.y(stageY);
  }

  private setStageToInitialSizeForExport<T>(exportFn: (exportWidth: number, exportHeight: number) => T) {
    this.deselect();
    const { width: exportWidth, height: exportHeight } = this.getBorderDimensions();

    // The Stage must be set on initial and full width and height, to render all elements correct.
    const scaleX = this.stage.scaleX();
    const scaleY = this.stage.scaleY();
    const stageHeight = this.stage.height();
    const stageWidth = this.stage.width();
    const stageX = this.stage.x();
    const stageY = this.stage.y();

    this.stage.scaleX(1);
    this.stage.scaleY(1);
    this.stage.height(exportHeight);
    this.stage.width(exportWidth);
    this.stage.x(0);
    this.stage.y(0);

    // prevent export from showing inner half of border
    this.floorplanBorderShape.strokeEnabled(false);

    // cover edges of stage which cause black border on export to dataUrl
    this.floorplanBorderShape.fill("#ffffff");
    this.floorplanBorderShape.fillEnabled(true);
    const exportResult = exportFn(exportWidth, exportHeight);

    this.floorplanBorderShape.strokeEnabled(true);
    this.floorplanBorderShape.fillEnabled(false);

    return { scaleX, scaleY, stageHeight, stageWidth, stageX, stageY, exportResult };
  }

  private exportFloorplanWithFallback(exportWidth: number, exportHeight: number) {
    const pixelRatios = [];
    if (this.isDataUrlVectorGraphic(this.floorplan.fileUrl)) {
      pixelRatios.push(3, 2, 1);
    } else {
      pixelRatios.push(1);
    }
    for (const ratio of pixelRatios) {
      try {
        const dataURL = this.exportFloorplanAtPixelRatio(exportWidth, exportHeight, ratio);
        if (dataURL && dataURL.length > 100) {
          return dataURL;
        }
      } catch (error) {
        console.warn(`Export failed with pixelRatio=${ratio}`, error);
        continue;
      }
      console.warn(`Export failed / too small with pixelRatio=${ratio}`);
    }
    throw new Error("Failed to export image with pixel ratios 3, 2, or 1.");
  }

  private isDataUrlVectorGraphic(dataUrl: string | undefined): boolean {
    if (!dataUrl) return false;
    const match = dataUrl.match(/^data:(.*?);base64,/i);
    if (!match) return false;

    const mime = match[1].toLowerCase();
    return mime === "image/svg+xml";
  }

  private exportFloorplanAtPixelRatio(exportWidth: number, exportHeight: number, pixelRatio: number): string {
    const { backgroundCanvas, overlayCanvas } = this.exportLayerCanvases(exportWidth, exportHeight, pixelRatio, pixelRatio);
    const outputCanvas = document.createElement("canvas");
    outputCanvas.width = Math.ceil(exportWidth * pixelRatio);
    outputCanvas.height = Math.ceil(exportHeight * pixelRatio);

    const context = outputCanvas.getContext("2d");
    if (!context) {
      throw new Error("Failed to create a canvas context for floorplan export.");
    }

    context.drawImage(backgroundCanvas, 0, 0, outputCanvas.width, outputCanvas.height);
    context.drawImage(overlayCanvas, 0, 0, outputCanvas.width, outputCanvas.height);
    return outputCanvas.toDataURL("image/png");
  }

  private exportFloorplanLayers(exportWidth: number, exportHeight: number): FloorplanExportLayers {
    for (const pixelRatio of FloorplanWorkspaceKonvaAdapter.EXPORT_PIXEL_RATIOS) {
      try {
        const { backgroundCanvas, overlayCanvas } = this.exportLayerCanvases(exportWidth, exportHeight, pixelRatio, pixelRatio);
        const backgroundDataUrl = backgroundCanvas.toDataURL("image/png");
        const overlayDataUrl = overlayCanvas.toDataURL("image/png");
        if (backgroundDataUrl.length > 100 && overlayDataUrl.length > 100) {
          return { backgroundDataUrl, overlayDataUrl };
        }
      } catch (error) {
        console.warn(`Layer export failed with pixelRatio=${pixelRatio}`, error);
        continue;
      }
      console.warn(`Layer export failed / too small with pixelRatio=${pixelRatio}`);
    }
    throw new Error("Failed to export layers with pixel ratios 3, 2, or 1.");
  }

  private exportLayerCanvases(
    exportWidth: number,
    exportHeight: number,
    backgroundPixelRatio: number,
    overlayPixelRatio: number,
  ) {
    const crop = {
      x: this.floorplanBorderShape.x(),
      y: this.floorplanBorderShape.y(),
      width: exportWidth,
      height: exportHeight,
    };
    return {
      backgroundCanvas: this.floorplanLayer.toCanvas({ ...crop, pixelRatio: backgroundPixelRatio }),
      overlayCanvas: this.itemLayer.toCanvas({ ...crop, pixelRatio: overlayPixelRatio }),
    };
  }

  private setGroupVisibility<T>(group: Konva.Group, cls: ClassConstructor<T>, visible: boolean): void {
    if (group.visible() === visible) {
      return;
    }

    group.visible(visible);
    if (!visible && this.selectedNode?.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) instanceof cls) {
      this.transformer.clear();
    }
  }

  private setProductGroupItemTextVisibility(group: Konva.Group, visible: boolean): void {
    if (!group) return;
    const groupChip = group.findOne(".groupChip");
    if (groupChip) groupChip.visible(visible);

    const itemText = group.findOne(".itemText") as Konva.Group | null;
    if (itemText) {
      itemText.visible(visible);
      itemText.setAttr("labelHidden", !visible);
    }

    if (!visible) return;

    const floorplanItem = group.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanItem | undefined;
    if (floorplanItem) {
      IconKonva.refreshItemText(floorplanItem, group);
      return;
    }
    if (itemText) {
      FloorplanItemTextKonva.updateBackground(itemText);
    }
  }

  private setGroupItemTextVisibility(group: Konva.Group, visible: boolean): void {
    if (!group) return;
    group.getChildren().forEach((child) => {
      const itemText = (child as Konva.Group).findOne(".itemText") as Konva.Group | null;
      if (!itemText) return;
      itemText.visible(visible);
      itemText.setAttr("labelHidden", !visible);
    });
  }

  private applyGroupedIcon(imageNode: Konva.Image, floorplanItem: ProductTypesInGroup, isInGroup?: boolean) {
    const imageType = this.getGroupedIconType(floorplanItem);
    if (!imageType) {
      return;
    }

    this.imageFactory.setIcon$(imageType, imageNode, isInGroup).subscribe(() => {
      this.refreshItemText(floorplanItem);
    });
  }

  private getGroupItemImageNode(itemGroup: Konva.Group, floorplanItem: ProductTypesInGroup): Konva.Image | null {
    return floorplanItem instanceof FloorplanTransmitter ||
      floorplanItem instanceof FloorplanTransmitterPlaceholder ||
      floorplanItem instanceof FloorplanFlameDetector ||
      floorplanItem instanceof FloorplanFlameDetectorPlaceholder
      ? (((itemGroup.getChildren()[1] as Konva.Group)?.getChildren()[1] as Konva.Image) ?? null)
      : floorplanItem instanceof FloorplanKvm
        ? (KvmKonva.getTransmitterIcon(itemGroup) ?? null)
        : ((itemGroup.getChildren()[1] as Konva.Image) ?? null);
  }

  private forEachGroupVisual(
    group: Konva.Group,
    callback: (context: { itemGroup: Konva.Group; floorplanItem: ProductTypesInGroup; imageNode: Konva.Image }) => void,
  ) {
    group.getChildren().forEach((node) => {
      if (!(node instanceof Konva.Group)) {
        return;
      }

      const floorplanItem = node.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as ProductTypesInGroup;
      if (!this.groupHandlers.find((handler) => handler.match(floorplanItem))) {
        return;
      }

      const imageNode = this.getGroupItemImageNode(node, floorplanItem);
      if (!imageNode) {
        return;
      }

      callback({
        itemGroup: node,
        floorplanItem,
        imageNode,
      });
    });
  }

  private isProductImageCapable(floorplanItem: ProductTypesInGroup): floorplanItem is ProductImageTypes {
    return (
      !(floorplanItem instanceof FloorplanSimplePlaceholder) &&
      !(floorplanItem instanceof FloorplanGasWarningCenterPlaceholder) &&
      !(floorplanItem instanceof FloorplanTransmitterPlaceholder) &&
      !(floorplanItem instanceof FloorplanFlameDetectorPlaceholder)
    );
  }

  private getGroupedIconType(floorplanItem: ProductTypesInGroup) {
    if (floorplanItem instanceof FloorplanGasWarningCenterPlaceholder) {
      return ImageType.GAS_WARNING_CENTER_PLACEHOLDER_ICON;
    }

    if (floorplanItem instanceof FloorplanGasWarningCenter) {
      return ImageType.GAS_WARNING_CENTER_ICON;
    }

    if (floorplanItem instanceof FloorplanTransmitterPlaceholder) {
      return ImageType.TRANSMITTER_PLACEHOLDER_ICON;
    }

    if (floorplanItem instanceof FloorplanTransmitter) {
      return ImageType.TRANSMITTER_ICON;
    }

    if (floorplanItem instanceof FloorplanAlarmDevice) {
      return ImageType.ALARM_DEVICE_ICON;
    }

    if (floorplanItem instanceof FloorplanSignalElement) {
      return ImageType.SIGNAL_ELEMENT_ICON;
    }

    if (floorplanItem instanceof FloorplanPlasticSign) {
      return ImageType.PLASTIC_SIGN_ICON;
    }

    if (floorplanItem instanceof FloorplanPatientMonitor) {
      return ImageType.PATIENT_MONITOR_ICON;
    }

    if (floorplanItem instanceof FloorplanCentralStation) {
      return ImageType.CENTRAL_STATION_ICON;
    }

    if (floorplanItem instanceof FloorplanTelemetry) {
      return ImageType.TELEMETRY_ICON;
    }

    if (floorplanItem instanceof FloorplanFlameDetector) {
      return ImageType.FLAME_DETECTOR_ICON;
    }

    if (floorplanItem instanceof FloorplanFlameDetectorPlaceholder) {
      return ImageType.FLAME_DETECTOR_PLACEHOLDER_ICON;
    }

    if (floorplanItem instanceof FloorplanKvm) {
      return ImageType.KVM_ICON;
    }

    if (floorplanItem instanceof FloorplanDisplay) {
      return ImageType.DISPLAY_ICON;
    }

    if (floorplanItem instanceof FloorplanUtility) {
      return ImageType.UTILITY_ICON;
    }

    if (floorplanItem instanceof FloorplanSimplePlaceholder) {
      switch (floorplanItem.type) {
        case FloorplanPlaceholderType.ALARM_DEVICE:
          return ImageType.ALARM_DEVICE_PLACEHOLDER_ICON;
        case FloorplanPlaceholderType.PLASTIC_SIGN:
          return ImageType.PLASTIC_SIGN_PLACEHOLDER_ICON;
        case FloorplanPlaceholderType.SIGNAL_ELEMENT:
          return ImageType.SIGNAL_ELEMENT_PLACEHOLDER_ICON;
        case FloorplanPlaceholderType.PATIENT_MONITOR:
          return ImageType.PATIENT_MONITOR_PLACEHOLDER_ICON;
        case FloorplanPlaceholderType.CENTRAL_STATION:
          return ImageType.CENTRAL_STATION_PLACEHOLDER_ICON;
        case FloorplanPlaceholderType.TELEMETRY:
          return ImageType.TELEMETRY_PLACEHOLDER_ICON;
        case FloorplanPlaceholderType.KVM:
          return ImageType.KVM_PLACEHOLDER_ICON;
        case FloorplanPlaceholderType.DISPLAY:
          return ImageType.DISPLAY_PLACEHOLDER_ICON;
        case FloorplanPlaceholderType.UTILITY:
          return ImageType.UTILITY_PLACEHOLDER_ICON;
      }
    }

    return undefined;
  }

  private setNodeLockState(element: Konva.Node, locked: boolean): void {
    if (!element) return;
    this.updateNodeDraggable(element, !locked);

    if (this.selectedNode === element) {
      this.transformer.reset(this.selectedNode);
    }

    if (element instanceof Konva.Group) {
      element.getChildren().forEach((child) => this.setNodeLockState(child, locked));
    }
  }

  private initItemLayer(floorplanState: FloorplanState) {
    // Sets the order of layers, bottom to top.
    // FGDS zones
    this.itemLayer.add(this.exZoneGroup);
    this.itemLayer.add(this.dustExZoneGroup);
    this.itemLayer.add(this.wildcardZoneGroup);
    this.itemLayer.add(this.dangerAreaGroup);

    // Monitoring zones
    this.itemLayer.add(this.isolationRoomGroup);
    this.itemLayer.add(this.itRoomGroup);
    this.itemLayer.add(this.operatingTheatreGroup);
    this.itemLayer.add(this.procedureRoomGroup);
    this.itemLayer.add(this.standardRoomGroup);

    this.itemLayer.add(this.imageGroup);
    this.itemLayer.add(this.flameDetectorConeGroup);
    this.itemLayer.add(this.pipelineGroup);
    this.itemLayer.add(this.cableRouteGroup);
    this.itemLayer.add(this.protectiveTubeGroup);
    this.itemLayer.add(this.wireGroup);
    this.itemLayer.add(this.transmitterGroup);
    this.itemLayer.add(this.gasWarningCenterGroup);
    this.itemLayer.add(this.alarmDeviceGroup);
    this.itemLayer.add(this.signalElementGroup);
    this.itemLayer.add(this.plasticSignGroup);
    this.itemLayer.add(this.flameDetectorGroup);
    this.itemLayer.add(this.measurementLineGroup);
    this.itemLayer.add(this.referenceMeasure);
    this.itemLayer.add(this.airPathGroup);
    this.itemLayer.add(this.floorplanTextGroup);
    this.itemLayer.add(this.patientMonitorGroup);
    this.itemLayer.add(this.centralStationGroup);
    this.itemLayer.add(this.telemetryGroup);
    this.itemLayer.add(this.kvmGroup);
    this.itemLayer.add(this.displayGroup);
    this.itemLayer.add(this.utilityGroup);
    this.itemLayer.add(this.productGroups);
    this.itemLayer.add(this.freeTextGroup);

    this.setVisibilityState(floorplanState);
    this.transformer.transformers.forEach((transformer) => this.itemLayer.add(transformer));
    this.transformer.initMultiSelectBehavior();

    this.stage.add(this.itemLayer);
  }

  private initIconElement(
    floorplanItem:
      | FloorplanSimplePlaceholder
      | FloorplanTransmitterPlaceholder
      | FloorplanFlameDetectorPlaceholder
      | FloorplanProductItem<ProductConfiguration>
      | FloorplanText
      | FloorplanGasWarningCenterPlaceholder,
    icon: Konva.Image,
    group: Konva.Group,
    itemTextVisible: boolean,
    select: boolean = false,
    productGroup?: Konva.Group,
  ) {
    const node = IconKonva.init(floorplanItem, icon, this.imageFactory.createImage(ImageType.WARNING_SIGN_ICON), productGroup);
    this.initKonvaElement(icon, select);
    this.setProductGroupItemTextVisibility(node, itemTextVisible);

    this.addItem(floorplanItem, node, group);
  }

  initGroupIconElement(product: any, icon: Konva.Image, groupNode: Konva.Group, itemTextVisible: boolean, select: boolean) {
    this.productGroups.add(groupNode);
    this.initIconElement(product, icon, groupNode, itemTextVisible, select, groupNode);
  }

  private initKonvaElement(node: Konva.Node, select: boolean = false) {
    node.on("mouseenter", () => (this.stage.container().style.cursor = "grab"));
    node.on("mouseleave", () => (this.stage.container().style.cursor = "default"));
    node.on("mousedown", () => (this.stage.container().style.cursor = "grabbing"));
    node.on("mouseup", () => (this.stage.container().style.cursor = "grab"));
    node.on("click tap", () => this.selectNode(node, ItemSelectIntention.REGULAR));
    node.on("dblclick dbltap", () => {
      if (this.floorplan.floorplanState.selectionMode !== FloorplanSelectionMode.MULTI) {
        this.selectNode(node, ItemSelectIntention.EXTENDED_VIEW);
      }
    });
    node.on("dragend", () => this.updateAfterDrag(node));
    if (select) {
      this.selectNode(node, ItemSelectIntention.REGULAR);
    }
  }

  private initStageTouchHandler(floorplanState: FloorplanState) {
    //pinch and zoom gestures is a slightly different implementation since we need to find the
    //point between the two fingers and scale accordingly
    this.stage.on("touchmove", (event) => {
      event.evt.preventDefault();
      const touch1 = event.evt.touches[0];
      const touch2 = event.evt.touches[1];

      if (touch1 && touch2) {
        // if the stage was under Konva's drag&drop
        // we need to stop it, and implement our own pan logic with two pointers
        // this drag refers to the whole stage
        if (this.stage.isDragging()) {
          this.stage.stopDrag();
        }
        // this makes all node of the layer non-draggable temporarily
        this.temporaryDisabledNodes = this.itemLayer.children
          .filter((node) => node.draggable())
          .map((node) => node.draggable(false));

        //deselect all shapes to prevent moving while pinching/zooming, which could also have side effects
        this.transformer.clear();

        const containerPos = this.stage.container().getBoundingClientRect();

        const p1 = new Point(touch1.clientX - containerPos.x, touch1.clientY - containerPos.y);
        const p2 = new Point(touch2.clientX - containerPos.x, touch2.clientY - containerPos.y);

        if (!this.lastCenter) {
          this.lastCenter = this.getCenter(p1, p2);
          return;
        }
        const newCenter = this.getCenter(p1, p2);

        const dist = this.getDistance(p1, p2);
        if (!this.lastDist) {
          this.lastDist = dist;
        }
        const scale = this.stage.scaleX() * (dist / this.lastDist);

        if (scale > 5 || scale < 0.2) {
          return;
        }
        // local coordinates of center point
        const pointTo = new Point(
          (newCenter.x - this.stage.x()) / this.stage.scaleX(),
          (newCenter.y - this.stage.y()) / this.stage.scaleX(),
        );

        this.stage.scaleX(scale);
        this.stage.scaleY(scale);

        // calculate new position of the stage
        const dx = newCenter.x - this.lastCenter.x;
        const dy = newCenter.y - this.lastCenter.y;

        const newPos = new Point(newCenter.x - pointTo.x * scale + dx, newCenter.y - pointTo.y * scale + dy);

        this.stage.position(newPos);

        this.lastDist = dist;
        this.lastCenter = newCenter;
      }

      floorplanState.updateScaleAndPosition(this.stage.scaleX(), this.stage.x(), this.stage.y());
    });
    this.stage.on("touchend", () => {
      this.lastDist = 0;
      this.lastCenter = null;
      // now we need to re-enable KonvaJS drag&drop
      this.temporaryDisabledNodes.map((node) => node.draggable(true));
    });
  }

  private initStageWheelHandler(floorplanState: FloorplanState) {
    //wheel is mapped to multitouch trackpads e.g. on the MacBook pro, so we can use it to zoom
    this.stage.on("wheel", (e) => {
      // stop default scrolling
      e.evt.preventDefault();
      if (this.stage.isDragging()) {
        this.stage.stopDrag();
      }

      const zoomIn = e.evt.deltaY < 0; // how to scale? Zoom in? Or zoom out?

      let referencePoint = new Point(0, 0);
      const pointer = this.stage.getPointerPosition();
      if (pointer) referencePoint = new Point(pointer.x, pointer.y);

      if (zoomIn) {
        floorplanState.zoomIn(referencePoint);
      } else {
        floorplanState.zoomOut(referencePoint);
      }
    });
  }

  private initStageDragHandler(floorplanState: FloorplanState) {
    this.stage.on("dragstart", (e) => {
      const mouseEvent = e.evt as MouseEvent;
      if (floorplanState.selectionMode === FloorplanSelectionMode.MULTI && mouseEvent.button === 0 && !mouseEvent.shiftKey) {
        this.stage.stopDrag();
        this.stage.draggable(false);
      }
    });
    this.stage.on("dragend", (e) => {
      if (e.target === this.stage) {
        floorplanState.updatePosition(this.stage.x(), this.stage.y());
      }
    });
  }

  private initStageTapHandler() {
    this.stage.on("click tap", (e) => {
      if (this.suppressNextClick) {
        this.suppressNextClick = false;
        return;
      }
      if (e.target === this.stage || e.target === this.floorplanShape) {
        this.deselect();
        return;
      }
    });
  }

  private clientToStagePos(clientX: number, clientY: number): { x: number; y: number } {
    const containerRect = this.stage.container().getBoundingClientRect();
    return {
      x: (clientX - containerRect.left - this.stage.x()) / this.stage.scaleX(),
      y: (clientY - containerRect.top - this.stage.y()) / this.stage.scaleY(),
    };
  }

  private initSelectionRectHandler(floorplanState: FloorplanState) {
    this.stage.on("mousedown", (e) => {
      if (floorplanState.selectionMode !== FloorplanSelectionMode.MULTI) {
        this.stage.draggable(true);
        return;
      }
      if (e.target !== this.stage && e.target !== this.floorplanShape) return;

      // Only handle left mouse button (button 0) for drag-selection
      // Middle button (1) and right button (2) should not trigger selection
      // Shift+left-click is reserved for panning
      const isLeftMouseButton = e.evt.button === 0;
      const isShiftPressed = e.evt.shiftKey;

      if (!isLeftMouseButton) {
        // Allow default behavior for other mouse buttons
        this.stage.draggable(true);
        return;
      }

      if (isShiftPressed) {
        // Shift+left-click: allow stage dragging for panning
        // Don't intercept the event, let Konva's default drag handling take over
        this.stage.draggable(true);
        return;
      }

      this.clearMultiSelectVisuals();
      this.selectedNodes.forEach((n) => this.updateNodeDraggable(n, false));
      this.selectedNodes.clear();
      this.selectedNode = undefined;
      this.transformer.clear();

      this.stage.stopDrag();
      this.stage.draggable(false);
      this.isDragSelecting = false;
      this.suppressNextClick = false;

      const pos = this.clientToStagePos(e.evt.clientX, e.evt.clientY);
      this.selectionRectStart = pos;

      this.selectionRectMouseMoveHandler = (ev: MouseEvent) => {
        if (!this.selectionRectStart) return;

        const cur = this.clientToStagePos(ev.clientX, ev.clientY);
        const start = this.selectionRectStart;

        const x = Math.min(start.x, cur.x);
        const y = Math.min(start.y, cur.y);
        const width = Math.abs(cur.x - start.x);
        const height = Math.abs(cur.y - start.y);

        if (!this.isDragSelecting && (width > 3 || height > 3)) {
          this.isDragSelecting = true;
          if (!this.selectionRect) {
            this.selectionRect = new Konva.Rect({
              fill: "rgba(78, 173, 255, 0.1)",
              stroke: "#4EADFF",
              strokeWidth: 1,
              strokeScaleEnabled: false,
              listening: false,
              visible: false,
            });
            this.itemLayer.add(this.selectionRect);
          }
        }

        if (this.isDragSelecting && this.selectionRect) {
          this.selectionRect.setAttrs({ x, y, width, height, visible: true });
          this.selectionRect.moveToTop();
        }
      };

      this.selectionRectMouseUpHandler = (ev: MouseEvent) => {
        if (this.selectionRectMouseMoveHandler) {
          window.removeEventListener("mousemove", this.selectionRectMouseMoveHandler);
          this.selectionRectMouseMoveHandler = null;
        }
        if (this.selectionRectMouseUpHandler) {
          window.removeEventListener("mouseup", this.selectionRectMouseUpHandler);
          this.selectionRectMouseUpHandler = null;
        }

        if (!this.selectionRectStart) return;

        if (this.isDragSelecting && this.selectionRect) {
          const cur = this.clientToStagePos(ev.clientX, ev.clientY);
          const start = this.selectionRectStart;

          const selBox = {
            x: Math.min(start.x, cur.x),
            y: Math.min(start.y, cur.y),
            width: Math.abs(cur.x - start.x),
            height: Math.abs(cur.y - start.y),
          };

          this.nodesByFloorplanItem.forEach((node, item) => {
            const outerRect = node.getClientRect({ relativeTo: this.itemLayer });
            if (this.rectsIntersect(selBox, outerRect)) {
              if (item instanceof ReferenceMeasure) return;
              const selectableNode = this.resolveSelectableNode(node, item);
              if (!this.selectedNodes.has(selectableNode)) {
                this.selectedNodes.add(selectableNode);
                this.selectedNode = selectableNode;
                const konvaItem = selectableNode.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY);
                this.updateNodeDraggable(selectableNode, !konvaItem?.locked);
                this.transformer.addToMultiSelect(selectableNode);
                this.addMultiSelectVisuals(selectableNode);
              }
            }
          });

          // Also iterate through KVM receivers
          this.nodesByKvmReceiver.forEach((node, receiver) => {
            const outerRect = node.getClientRect({ relativeTo: this.itemLayer });
            if (this.rectsIntersect(selBox, outerRect)) {
              const selectableNode = this.resolveSelectableNode(node, receiver);
              if (!this.selectedNodes.has(selectableNode)) {
                this.selectedNodes.add(selectableNode);
                this.selectedNode = selectableNode;
                this.updateNodeDraggable(selectableNode, !receiver.locked);
                this.transformer.addToMultiSelect(selectableNode);
                this.addMultiSelectVisuals(selectableNode);
              }
            }
          });

          // Also iterate through remote sensors (GWC sensors in FGDS)
          this.nodesByRemoteSensor.forEach((node, remoteSensor) => {
            const outerRect = node.getClientRect({ relativeTo: this.itemLayer });
            if (this.rectsIntersect(selBox, outerRect)) {
              const selectableNode = this.resolveSelectableNode(node, remoteSensor);
              if (!this.selectedNodes.has(selectableNode)) {
                this.selectedNodes.add(selectableNode);
                this.selectedNode = selectableNode;
                this.updateNodeDraggable(selectableNode, !remoteSensor.locked);
                this.transformer.addToMultiSelect(selectableNode);
                this.addMultiSelectVisuals(selectableNode);
              }
            }
          });

          const selectedItems = [...this.selectedNodes]
            .map((n) => n.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY))
            .filter((i): i is FloorplanItem => i instanceof FloorplanItem);
          this._selectedItemChanged$.next({
            item: selectedItems[selectedItems.length - 1] ?? null,
            selectIntention: ItemSelectIntention.REGULAR,
          });
          this._selectedItemsChanged$.next(selectedItems);

          this.selectionRect.visible(false);
          this.isDragSelecting = false;
          this.suppressNextClick = true;
        }

        this.selectionRectStart = null;
        this.stage.draggable(floorplanState.selectionMode !== FloorplanSelectionMode.MULTI);
      };

      window.addEventListener("mousemove", this.selectionRectMouseMoveHandler);
      window.addEventListener("mouseup", this.selectionRectMouseUpHandler);
    });
  }

  private rectsIntersect(
    a: { x: number; y: number; width: number; height: number },
    b: { x: number; y: number; width: number; height: number },
  ): boolean {
    return !(a.x + a.width < b.x || b.x + b.width < a.x || a.y + a.height < b.y || b.y + b.height < a.y);
  }

  private initResizeHandler() {
    window.addEventListener("resize", () => {
      this.stage.width(this.stage.container().clientWidth);
      this.stage.height(this.stage.container().clientHeight);
    });
  }

  private initDragOverHandler() {
    this.stage.container().addEventListener("dragover", function (e) {
      e.preventDefault();
    });
  }

  private initDropHandler() {
    this.stage.container().addEventListener("drop", (e: DragEvent) => {
      e.preventDefault();
      this.dropFloorplanItem(e);
    });
  }

  private dropFloorplanItem(e: DragEvent) {
    const rect = this.stage.container().getBoundingClientRect();

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Correct for scale and pan
    const scale = this.stage.scaleX();
    const stageX = (x - this.stage.x()) / scale;
    const stageY = (y - this.stage.y()) / scale;

    this.dragDropService.publishDropEvent({
      x: stageX,
      y: stageY,
    });
  }

  private getCenter(p1: any, p2: any) {
    return {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
  }

  private getDistance(p1: Point, p2: Point) {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  }

  private updateAfterDrag(node: Konva.Node) {
    const floorplanItem = node.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) as FloorplanNode;
    floorplanItem.updatePosition(node.x(), node.y());
  }

  private updateNodeDraggable(node: Konva.Node, draggable: boolean) {
    if (node.getAttrs().draggable === undefined) {
      // in case draggable is not specified, the draggable property should never be touched, e.g. monitoring-area
      return;
    }
    node.setAttr("draggable", draggable);
  }

  private selectNode(node: Konva.Node, selectIntention: ItemSelectIntention) {
    const item = node.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY);
    const isMultiMode = this.floorplan.floorplanState.selectionMode === FloorplanSelectionMode.MULTI;

    if (isMultiMode && selectIntention === ItemSelectIntention.REGULAR) {
      if (item instanceof ReferenceMeasure) return;
      if (this.selectedNodes.has(node)) {
        // Toggle off: deselect this node
        this.updateNodeDraggable(node, false);
        this.selectedNodes.delete(node);
        this.transformer.remove(node);
        this.removeMultiSelectVisuals(node);
        if (this.selectedNode === node) {
          // Move primary selection to another node in the set if available
          this.selectedNode = this.selectedNodes.size > 0 ? [...this.selectedNodes][this.selectedNodes.size - 1] : undefined;
        }
      } else {
        this.selectedNodes.add(node);
        this.selectedNode = node;
        this.updateNodeDraggable(node, !item.locked);
        this.transformer.addToMultiSelect(node);
        this.addMultiSelectVisuals(node);
      }
      const selectedItems = [...this.selectedNodes]
        .map((n) => n.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY))
        .filter((i): i is FloorplanItem => i instanceof FloorplanItem);
      this._selectedItemChanged$.next({ item: selectedItems[selectedItems.length - 1] ?? null, selectIntention });
      this._selectedItemsChanged$.next(selectedItems);
      return;
    }

    // Single-select mode (default)
    this.clearMultiSelectVisuals();
    if (this.selectedNode) this.updateNodeDraggable(this.selectedNode, false);
    this.selectedNodes.forEach((n) => this.updateNodeDraggable(n, false));
    this.selectedNodes.clear();

    this.selectedNode = node;
    this.selectedNodes.add(node);
    if (item instanceof FloorplanItem) {
      this._selectedItemChanged$.next({ item, selectIntention });
    }
    this._selectedItemsChanged$.next(item instanceof FloorplanItem ? [item] : []);
    this.updateNodeDraggable(node, !item.locked);
    this.transformer.reset(node);
  }

  private addItem(floorplanItem: FloorplanItem, node: Konva.Shape | Konva.Group, group: Konva.Group) {
    group.add(node);
    this.nodesByFloorplanItem.set(floorplanItem, node);
    if (this.pendingSelection?.item === floorplanItem) {
      const selectIntention = this.pendingSelection.selectIntention;
      this.pendingSelection = undefined;
      this.selectNode(this.resolveSelectableNode(node, floorplanItem), selectIntention);
    }
  }

  private resolveSelectableNode(node: Konva.Node, floorplanItem: FloorplanItem | FloorplanKvmReceiver | FloorplanRemoteSensor) {
    if (node.getAttrs().draggable !== undefined) {
      return node;
    }

    if (!(node instanceof Konva.Group)) {
      return node;
    }

    const selectableNode = node.find((candidate: Konva.Node) => {
      return (
        candidate.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) === floorplanItem &&
        candidate.getAttrs().draggable !== undefined
      );
    })[0];

    return selectableNode ?? node;
  }

  private getKvmReceiverGroups(kvmGroup: Konva.Group) {
    return kvmGroup
      .getChildren()
      .filter(
        (child): child is Konva.Group =>
          child instanceof Konva.Group &&
          child.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) instanceof FloorplanKvmReceiver,
      );
  }

  private isNodeWithin(ancestor: Konva.Node, node?: Konva.Node) {
    let currentNode = node;

    while (currentNode) {
      if (currentNode === ancestor) {
        return true;
      }

      currentNode = currentNode.getParent() ?? undefined;
    }

    return false;
  }

  private resolveProductGroupNode(product: ProductTypesInGroup, groupNode?: Konva.Group) {
    const currentGroup = this.floorplan.project.groups.find((g) =>
      g.references.some((r) => r.floorplanProductItemId === product.id),
    );
    if (currentGroup) {
      const existingGroupNode = this.findKonvaGroup(currentGroup);
      if (existingGroupNode) {
        return existingGroupNode;
      }
      // The group was created on another floorplan – create the Konva node lazily so the chip is rendered.
      const newGroupNode = GroupKonva.init(currentGroup);
      this.productGroups.add(newGroupNode);
      return newGroupNode;
    }

    return groupNode;
  }

  private trackProductImageLoad<T>(load$: Observable<T>): Observable<T> {
    const shared$ = load$.pipe(shareReplay(1));
    this.pendingProductImageLoads.push(
      shared$.pipe(
        map(() => undefined),
        catchError(() => of(undefined)),
      ),
    );
    return shared$;
  }

  private trackFloorplanImageLoad<T>(load$: Observable<T>): Observable<T> {
    const shared$ = load$.pipe(shareReplay(1));
    this.pendingFloorplanImageLoads.push(
      shared$.pipe(
        map(() => undefined),
        catchError(() => of(undefined)),
      ),
    );
    return shared$;
  }
}
